import { userLabel } from './user-identity.js';
/**
 * ERP (경영 관리) 클라이언트: 문서 편집기(줄 항목·자동 계산·품목 피커·부가세 모드·문서 할인·
 * 명시적 저장·dirty 스냅샷·상태 흐름 스테퍼·취소/거절 사유·잠금/해제·수주/청구 전환 토스트·이력 카드·담당 직원/수신 담당자 피커),
 * 문서 목록(담당 칩·키보드·우클릭), 품목 마스터 제자리 편집, /admin/erp 설정 페이지.
 * 피드백은 showToast / setSaveState 만 쓴다(모듈 전용 토스트 없음, spec 2.1 ⑫).
 * 문자열은 <script id="erpL10n"> JSON 블록에서 읽는다.
 */
import { initInappSelects, syncSelect } from './inapp-select.js';
import { appConfirm } from './confirm-dialog.js';
import { showContextMenu } from './context-menu.js';
import { showToast, record, history, patchCommand, deleteCommand, createCommand, composite } from './calendar-history.js';
import { setSaveState } from './utils.js';
import { attachDatePicker, syncDateLabel } from './datepicker.js';
import { openPeoplePicker, openContactPicker, personEl, peopleIndex } from './people-picker.js';
import { navigate } from './calendar-spa.js';
import { openPillPicker } from './prop-picker.js';
import { setupSavedViews, setupColumnHiding, savePref, pagePrefs } from './saved-views.js';
import { setupERPLines } from './erp-lines.js';
import { setupERPApproval } from './erp-approval.js';
import { setupERPPayments, buildQuickPayRow } from './erp-payments.js';

function l10n() {
    const el = document.getElementById('erpL10n');
    if (!el) return { errors: {} };
    try { const o = JSON.parse(el.textContent || '{}'); o.errors = o.errors || {}; return o; } catch (_) { return { errors: {} }; }
}
function csrf() { return window.getCSRFToken ? window.getCSRFToken() : ''; }
function post(url, fields, L) {
    return fetch(url, { method: 'POST', body: new URLSearchParams(fields || {}), credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-Token': csrf() } })
        .then(async (r) => {
            if (!r.ok) {
                let m = 'HTTP ' + r.status, body = null;
                try { body = await r.json(); m = body.message || m; } catch (_) { /* */ }
                const err = new Error((L && L.errors && L.errors[m]) || m); err.status = r.status; err.body = body; err.key = m;
                if (r.status === 401) { // 세션 만료: 재로그인 안내 (QA spec-4)
                    const l = L || l10n();
                    showToast(l.sessionExpired || '세션이 만료되었습니다 — 다시 로그인하세요', { error: true, actionLabel: l.login || '로그인', onAction: () => { location.href = '/login?next=' + encodeURIComponent(location.pathname + location.search); }, duration: 15000 });
                    err.handled = true;
                }
                throw err;
            }
            return r.json();
        });
}
const money = (n) => (Number(n) || 0).toLocaleString('ko-KR');
const num = (v) => Number(String(v).replace(/[^0-9.-]/g, '')) || 0;
function inField() {
    const a = document.activeElement; if (!a) return false;
    const tag = a.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!a.isContentEditable;
}
function fail(L, e) { if (e && e.handled) return; showToast((e && e.message) || (L && L.failed) || 'failed', { error: true }); }
// 실행 취소 스택용 API (calendar-history 의 patchCommand/deleteCommand/createCommand 가 쓰는 patch/remove/restore 3종).
// 문서는 /save 가 patch 역할, 품목은 /patch. 오류 문구는 post() 가 L.errors 로 번역한다.
function erpApi(base, L, patchAction) {
    const url = (id, action) => base + '/' + encodeURIComponent(id) + '/' + action;
    return {
        patch: (id, fields) => post(url(id, patchAction), fields, L),
        remove: (id) => post(url(id, 'delete'), {}, L),
        restore: (id) => post(url(id, 'restore'), {}, L),
    };
}
function personOf(id) {
    const idx = peopleIndex();
    const p = idx.byId[id];
    if (!p) return null;
    return Object.assign({}, p, { me: id === idx.me });
}
function contactPill(c) {
    const s = document.createElement('span');
    s.className = 'person md contact';
    const i = document.createElement('i'); i.className = 'rec-avatar'; i.textContent = c.name ? [...String(c.name).trim()][0].toUpperCase() : '?';
    const b = document.createElement('b'); b.className = 'person-name'; b.textContent = c.name || '';
    if (c.title) { b.appendChild(document.createTextNode(' ')); const r = document.createElement('span'); r.className = 'person-rank'; r.textContent = c.title; b.appendChild(r); }
    const small = document.createElement('small'); small.textContent = [c.email, c.phone].filter(Boolean).join(' · ');
    s.appendChild(i); s.appendChild(b); s.appendChild(small);
    return s;
}
// 공급받는자 스냅샷 블록 (E3): 저장 응답의 bill_to 로 다시 그린다.
function renderBillTo(row, b, accountId) {
    const text = row.querySelector('#erpBillToText'); if (!text) return;
    text.innerHTML = '';
    if (!b || !b.name) { row.classList.add('empty'); const m = document.createElement('span'); m.className = 'rec-muted'; m.textContent = '—'; text.appendChild(m); return; }
    row.classList.remove('empty');
    const sep = () => { const s = document.createElement('span'); s.className = 'erp-meta-sep'; s.textContent = ' · '; return s; };
    const name = document.createElement('b'); name.textContent = b.name; text.appendChild(name);
    if (b.biz_no) { text.appendChild(sep()); const n = document.createElement('span'); n.className = 'mono'; n.textContent = b.biz_no; text.appendChild(n); }
    if (b.ceo) { text.appendChild(sep()); text.appendChild(document.createTextNode((row.dataset.lCeo || '') + ' ' + b.ceo)); }
    if (b.address) { text.appendChild(document.createElement('br')); const a = document.createElement('small'); a.className = 'rec-muted'; a.textContent = b.address; text.appendChild(a); }
    if (!b.biz_no) { text.appendChild(document.createElement('br')); const w = document.createElement('a'); w.className = 'erp-bill-to-warn'; w.href = '/crm/accounts/' + encodeURIComponent(accountId || ''); w.dataset.noSpa = ''; w.textContent = '⚠ ' + (row.dataset.lMissing || ''); text.appendChild(w); }
}
function addDays(iso, days) {
    const d = iso ? new Date(iso + 'T00:00:00') : new Date();
    d.setDate(d.getDate() + days);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
function monthEnd(iso) {
    const d = iso ? new Date(iso + 'T00:00:00') : new Date();
    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return e.getFullYear() + '-' + String(e.getMonth() + 1).padStart(2, '0') + '-' + String(e.getDate()).padStart(2, '0');
}

// 목록·대시보드 행: 행 클릭 = 열기 (행은 <div> — 안의 <a>(번호·담당 직원 이름)는 그대로 둔다).
// 편집기는 다른 루트(#erpDoc)를 가진 다른 경로라 SPA 부분 교체 대상이 아니다 → 전체 로드.
// 섹션 안 이동(목록 ↔ 편집기, 새 문서, 복제)은 SPA: main.admin-main 을 통째로 바꾸고 모듈을 다시 초기화한다.
// 문서/창 수준 리스너는 AbortController 로 묶는다 (onDoc/onWin); 요소 리스너는 요소와 함께 사라진다.
let erpTeardown = null;
let sig = null;
const optsOf = (o) => Object.assign({ signal: sig }, typeof o === 'boolean' ? { capture: o } : (o || {}));
const onDoc = (type, fn, o) => document.addEventListener(type, fn, optsOf(o));
const onWin = (type, fn, o) => window.addEventListener(type, fn, optsOf(o));
document.addEventListener('calendar:navigated', (e) => { if (e.detail && e.detail.crossPath) setupERP(); });
function openRow(href) { if (href) navigate(href); }
// 편집기 재로드 (상태 전이로 카드 구성이 바뀔 때): 같은 주소를 SPA 로 다시 그린다
function reloadDoc() { return navigate(location.pathname + location.search, { swap: true, replace: true }); }
function setupRowOpen() {
    onDoc('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const row = e.target.closest('.erp-page .rec-row[data-href]'); if (!row) return;
        if (e.target.closest('a, button, input, select, textarea, label')) return;
        e.preventDefault(); openRow(row.dataset.href);
    });
    onDoc('keydown', (e) => {
        if (e.key !== 'Enter' || inField()) return;
        const row = e.target.closest && e.target.closest('.erp-page .rec-row[data-href]'); if (!row) return;
        e.preventDefault(); openRow(row.dataset.href);
    });
}

export function setupERP() {
    if (erpTeardown) { erpTeardown(); erpTeardown = null; }
    const L = l10n();
    if (!document.querySelector('.erp-page')) return;
    const ctl = new AbortController();
    sig = ctl.signal;
    erpTeardown = () => { ctl.abort(); document.body.classList.remove('modal-scroll-lock'); };
    peopleIndex(!!document.getElementById('peopleData'));
    setupRowOpen();
    setupScopeChips();
    const doc = document.getElementById('erpDoc');
    const list = document.getElementById('recList');
    const settings = document.getElementById('erpSettings');
    if (doc) setupDocEditor(doc, L);
    if (list && list.dataset.kind === 'item') setupItemList(list, L);
    if (list && list.dataset.kind === 'doc') setupDocList(list, L);
    const itemPrices = document.getElementById('erpItemPrices'); // C-4 §8.3 품목 상세의 거래처별 단가 카드
    if (itemPrices) setupItemPrices(itemPrices, L);
    const dashOutstanding = document.querySelector('.erp-dash-outstanding');
    if (dashOutstanding) setupPayContextMenu(dashOutstanding, L);
    if (settings) setupERPSettings(settings, L);
    const ledger = document.getElementById('erpLedger'); // C-3 §6.3
    // F15: an SPA refresh can land a still-pending first import() after a
    // second setupERP() has already swapped in fresh markup — without this
    // staleness check both callbacks bind the same new #erpLedgerPay button
    // (it lives in .crm-head, outside the #erpLedger root) and one click opens
    // two overlapping dialogs.
    if (ledger) import('./erp-ledger.js').then((m) => { if (document.getElementById('erpLedger') === ledger) m.setupERPLedger(ledger, { L, post }); });
    const approvals = document.getElementById('erpApprovals'); // 다섯 모듈 UX §9.3 승인함 제자리 결재
    if (approvals) import('./erp-approvals.js').then((m) => { if (document.getElementById('erpApprovals') === approvals) m.setupERPApprovals(approvals, { L, post }); });
    const reports = document.getElementById('erpReports'); // C-4 §7
    if (reports) import('./erp-reports.js').then((m) => { if (document.getElementById('erpReports') === reports) m.setupERPReports(reports); });
}

// 사유 입력 팝오버 (.prop-pop): appConfirm 에 입력 옵션이 없어 인라인 폼으로 (편집기 · 목록 공용)
function reasonPop(anchor, L, opts) {
    const old = document.querySelector('.erp-reason-pop'); if (old) old.remove();
    const pop = document.createElement('div'); pop.className = 'prop-pop erp-reason-pop'; pop.setAttribute('role', 'dialog');
    const h = document.createElement('div'); h.className = 'erp-reason-title'; h.textContent = opts.title; pop.appendChild(h);
    const ta = document.createElement('textarea'); ta.className = 'form-input erp-reason-input'; ta.maxLength = 500; ta.rows = 2; ta.placeholder = L.reasonPlaceholder || ''; pop.appendChild(ta);
    const row = document.createElement('div'); row.className = 'erp-reason-actions';
    const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'btn btn-secondary btn-sm'; cancel.textContent = L.cancel || '취소';
    const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-sm ' + (opts.danger ? 'btn-error' : 'btn-primary'); ok.textContent = opts.okLabel;
    row.appendChild(cancel); row.appendChild(ok); pop.appendChild(row);
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - pop.offsetWidth - 8)) + 'px';
    pop.style.top = Math.min(r.bottom + 4, window.innerHeight - pop.offsetHeight - 8) + 'px';
    const close = () => { pop.remove(); document.removeEventListener('mousedown', outside, true); };
    const outside = (e) => { if (!pop.contains(e.target)) close(); };
    setTimeout(() => onDoc('mousedown', outside, true), 0);
    cancel.addEventListener('click', close);
    ok.addEventListener('click', () => { const v = ta.value.trim(); close(); opts.onSubmit(v); });
    ta.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ok.click(); } });
    ta.focus();
}

// ── 문서 편집기 ──
// E6: 상태는 흐름 스테퍼(#erpFlow) + 허용된 다음 단계 버튼(#erpNextSteps)만; 취소/거절은 ⋯ → 사유 입력;
// 잠긴 문서는 입력 readOnly + 잠금 필, 번호 우클릭 → 잠금 해제(erp.pay|manage); 전환은 저장 후 토스트(열기 · 수주 표시);
// 이력 카드는 응답의 event 로 제자리 갱신. E7: 부가세 세그먼트(별도/포함/면세) · 문서 할인 · 줄 규격/단위/할인/세율 필.
const LOCKED = { quote: ['accepted'], order: ['delivered'], statement: ['issued', 'cancelled'], invoice: ['issued', 'cancelled'] };
const NEEDS_NOTE = { cancelled: true, rejected: true };
function setupDocEditor(doc, L) {
    initInappSelects(doc);
    let items = [];
    try { items = JSON.parse(doc.dataset.items || '[]') || []; } catch (_) { items = []; }
    let lines = [];
    try { lines = JSON.parse(doc.dataset.lines || '[]') || []; } catch (_) { lines = []; }
    let contact = null;
    try { contact = doc.dataset.contact ? JSON.parse(doc.dataset.contact) : null; } catch (_) { contact = null; }
    let accountPrices = {};
    try { accountPrices = JSON.parse(doc.dataset.accountPrices || '{}') || {}; } catch (_) { accountPrices = {}; }
    let transitions = { table: {}, labels: {}, flow: [] };
    try { transitions = Object.assign(transitions, JSON.parse(doc.dataset.transitions || '{}')); } catch (_) { /* */ }
    const $ = (id) => document.getElementById(id);
    const state = $('erpState'), saveBtn = $('erpSave'), discardBtn = $('erpDiscard');
    const title = $('erpTitle'), account = $('erpAccount'), accountBtn = $('erpAccountBtn'), memo = $('erpMemo');
    const ownerHidden = $('erpOwner'), ownerBtn = $('erpOwnerBtn');
    const contactHidden = $('erpContact'), contactBtn = $('erpContactBtn'), contactLinks = $('erpContactLinks');
    const issueHidden = $('erpIssue'), issueBtn = $('erpIssueBtn'), dueHidden = $('erpDue'), dueBtn = $('erpDueBtn');
    const validDays = Number(doc.dataset.validDays) || 30;
    const isNew = !doc.dataset.id;
    if (isNew) title.focus();
    const isDeleted = !!doc.dataset.deleted; // E8: 삭제된 문서는 읽기 전용 + 복원만 (저장·피커·단축키 없음)
    const kind = doc.dataset.kind;
    L = Object.assign({}, L, { lockedHint: kind === 'quote' ? L.lockedHintQuote : kind === 'order' ? L.lockedHintOrder : kind === 'statement' ? L.lockedHintStatement : L.lockedHint });
    const canManage = !!doc.dataset.canManage, canPay = !!doc.dataset.canPay;
    let locked = !!doc.dataset.locked, canUnlock = !!doc.dataset.canUnlock, hasUnlock = !!doc.dataset.hasUnlock;
    let taxMode = doc.dataset.taxMode || 'exclusive';
    const discountRow = $('erpDiscountRow'), discountInput = $('erpDiscountInput'), discountAdd = $('erpDiscountAdd');
    const discountValue = () => (discountInput ? Math.max(0, Math.round(num(discountInput.value))) : 0);

    // 줄 편집기 (E7: 계산은 models.LineTotals 미러 — tax_mode · rounding · 문서 할인 주입)
    let colHidden = [];
    try { const h = JSON.parse(pagePrefs()['erp.cols.doc'] || '[]'); if (Array.isArray(h)) colHidden = h; } catch (_) { colHidden = []; }
    const linesApi = setupERPLines($('erpLineRows'), {
        items, lines, L, money, num, taxDefault: Number(doc.dataset.defaultTax) || 10,
        taxMode: () => taxMode, rounding: doc.dataset.taxRounding || 'floor', docDiscount: discountValue,
        locked, canManage, showDiscount: !colHidden.includes('discount'), // §9: 기본 표시, 숨김은 사용자 설정
        accountPrices: accountPrices, // C-4 §8.3
        post: (url, fields) => post(url, fields, L), onChange: () => checkDirty(),
    });
    $('erpAddLine').addEventListener('click', () => { linesApi.addLine(); });

    // 부가세 세그먼트 (별도 / 포함 / 면세) — 포함이면 단가 헤더가 바뀐다
    const taxSeg = $('erpTaxModeSeg'), priceHead = $('erpUnitPriceHead');
    function applyTaxMode(mode) {
        taxMode = mode;
        if (taxSeg) taxSeg.querySelectorAll('[data-mode]').forEach((b) => b.classList.toggle('active', b.dataset.mode === mode));
        if (priceHead) priceHead.textContent = mode === 'inclusive' ? (priceHead.dataset.lIncl || '단가(VAT포함)') : (priceHead.dataset.lExcl || '단가');
        doc.dataset.taxMode = mode;
        linesApi.recalc();
    }
    if (taxSeg) taxSeg.addEventListener('click', (e) => {
        const b = e.target.closest('[data-mode]'); if (!b || b.disabled || locked) return;
        applyTaxMode(b.dataset.mode); checkDirty();
    });
    // 문서 할인 (0 이면 접힘 → ＋ 할인)
    function syncDiscountRow() { if (discountRow) discountRow.classList.toggle('empty', !discountValue() && document.activeElement !== discountInput); }
    if (discountAdd) discountAdd.addEventListener('click', () => { discountRow.classList.remove('empty'); discountInput.focus(); });
    if (discountInput) {
        discountInput.addEventListener('input', () => { linesApi.recalc(); checkDirty(); });
        discountInput.addEventListener('blur', () => { discountInput.value = discountValue() ? money(discountValue()) : ''; syncDiscountRow(); });
        discountInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); discountInput.blur(); } });
    }

    // 담당 직원 (users.id → person 파셜과 같은 마크업)
    function renderOwner() {
        const id = ownerHidden.value;
        const p = id ? personOf(id) : null;
        ownerBtn.innerHTML = '';
        if (p) { ownerBtn.appendChild(personEl(p, 'md')); ownerBtn.classList.remove('empty'); }
        else if (id) { const s = document.createElement('span'); s.className = 'person md inactive'; s.textContent = id; ownerBtn.appendChild(s); ownerBtn.classList.remove('empty'); }
        else { ownerBtn.textContent = '—'; ownerBtn.classList.add('empty'); }
    }
    function setOwner(id) { ownerHidden.value = id || ''; renderOwner(); checkDirty(); }
    function openOwner() {
        openPeoplePicker(ownerBtn, { values: ownerHidden.value ? [ownerHidden.value] : [], allowNone: true, allowMe: true, labels: { me: L.me, none: L.none }, onChange: (ids) => setOwner(ids[0] || '') });
    }
    ownerBtn.addEventListener('click', () => { if (!isDeleted) openOwner(); });
    if (isDeleted) { ownerBtn.disabled = true; contactBtn.disabled = true; }
    if (peopleIndex().loaded) renderOwner(); else peopleIndex().ready.then(renderOwner);

    // 수신 담당자 (crm_contacts)
    function renderContact() {
        contactBtn.innerHTML = ''; contactLinks.innerHTML = '';
        if (!contact) { contactBtn.textContent = '—'; contactBtn.classList.add('empty'); return; }
        contactBtn.classList.remove('empty');
        contactBtn.appendChild(contactPill(contact));
        const add = (href, text, ttl, noSpa) => { const a = document.createElement('a'); a.href = href; a.textContent = text; a.title = ttl; a.className = 'erp-icon-link'; if (noSpa) a.dataset.noSpa = ''; contactLinks.appendChild(a); };
        if (contact.email) add('mailto:' + contact.email, '✉', contact.email);
        if (contact.phone) add('tel:' + String(contact.phone).replace(/[^+\d]/g, ''), '☎', contact.phone);
        if (contact.account_id) add('/crm/accounts/' + encodeURIComponent(contact.account_id) + '#contacts', '↗', L.recipient || '', true);
    }
    function setContact(c) { contact = c || null; contactHidden.value = c ? c.id : ''; renderContact(); checkDirty(); }
    contactBtn.addEventListener('click', () => {
        if (isDeleted) return;
        openContactPicker(contactBtn, { accountId: account.value, value: contactHidden.value, allowNew: true, labels: { pickAccount: L.pickAccountFirst, newPrefix: L.newContact, none: L.none }, onChange: (c) => setContact(c) });
    });
    renderContact();
    // 거래처 = 검색 피커 + 「＋ 새 거래처」 (다섯 모듈 UX §9.2, G5): 값은 숨은 #erpAccount 에, 라벨은 버튼에.
    // 예전 <select> 처럼 #erpAccount 에 value 를 넣고 change 를 쏘면 그대로 동작한다 (e2e · 다른 모듈).
    function renderAccount(name) {
        if (!accountBtn) return;
        if (name !== undefined) accountBtn.dataset.name = name || '';
        const id = account.value;
        accountBtn.textContent = id ? (accountBtn.dataset.name || id) : '—';
        accountBtn.classList.toggle('empty', !id);
    }
    function setAccount(id, name) {
        account.value = id || ''; renderAccount(name || '');
        account.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (accountBtn) accountBtn.addEventListener('click', () => {
        if (locked || isDeleted || accountBtn.disabled) return;
        openAccountPicker(accountBtn, L, account.value, (a) => setAccount(a.id, a.name), { allowNone: true, allowNew: true });
    });
    // N11: 거래처를 A → B → A 로 빠르게 바꾸면 응답이 순서를 바꿔 도착할 수 있다.
    // 요청마다 토큰을 올리고 마지막 요청의 응답만 반영한다 — 아니면 A 문서에
    // B 의 단가가 채워진다.
    let priceReq = 0;
    account.addEventListener('change', () => {
        if (contact && contact.account_id !== account.value) setContact(null);
        checkDirty();
        // C-4 §8.3: 거래처가 바뀌면 그 거래처 단가를 다시 받아 피커에 넣는다
        const id = account.value;
        const want = ++priceReq;
        if (!id) { linesApi.setAccountPrices({}); }
        else {
            fetch('/api/erp/accounts/' + encodeURIComponent(id) + '/prices', { headers: { Accept: 'application/json' } })
                .then((r) => (r.ok ? r.json() : {})).then((m) => { if (want === priceReq) linesApi.setAccountPrices(m); }).catch(() => {});
        }
    });

    // 날짜: .att-dp 피커 + 프리셋
    [[issueBtn, issueHidden], [dueBtn, dueHidden]].forEach(([btn, hidden]) => {
        syncDateLabel(btn, hidden, L.pickDate);
        attachDatePicker(btn, hidden, { placeholder: L.pickDate, clearLabel: L.tbd, onSet: () => checkDirty() });
    });
    doc.querySelectorAll('.erp-preset').forEach((b) => b.addEventListener('click', () => {
        const base = issueHidden.value || '';
        const p = b.dataset.preset;
        dueHidden.value = p === 'eom' ? monthEnd(base) : addDays(base, p === 'valid' ? validDays : Number(p) || 30);
        syncDateLabel(dueBtn, dueHidden, L.pickDate);
        checkDirty();
    }));

    // dirty = 스냅샷 비교 (필드별 플래그 없음)
    function fields() {
        return { kind, title: title.value.trim(), account_id: account.value, issue_date: issueHidden.value, due_date: dueHidden.value, owner_id: ownerHidden.value, contact_id: contactHidden.value, memo: memo.value, lines: linesApi.serialize(), tax_mode: taxMode, discount_amount: String(discountValue()), parent_id: new URLSearchParams(location.search).get('from') || '', deal_id: isNew ? (new URLSearchParams(location.search).get('deal') || '') : (doc.dataset.dealId || '') };
    }
    const serialize = () => JSON.stringify(fields());
    let loaded = serialize(), loadedFields = fields(), loadedContact = contact, loadedLines = linesApi.collect(), loadedAccountName = accountBtn ? (accountBtn.dataset.name || '') : '';
    const dirty = () => serialize() !== loaded;
    function snapshot() { loaded = serialize(); loadedFields = fields(); loadedContact = contact; loadedLines = linesApi.collect(); loadedAccountName = accountBtn ? (accountBtn.dataset.name || '') : ''; checkDirty(); }
    function checkDirty() {
        const d = dirty();
        saveBtn.classList.toggle('dirty', d);
        saveBtn.querySelector('.erp-save-label').textContent = d && !isNew ? saveBtn.dataset.labelDirty : saveBtn.dataset.label;
        discardBtn.hidden = !d;
        return d;
    }
    function discard() {
        title.value = loadedFields.title; memo.value = loadedFields.memo;
        account.value = loadedFields.account_id; renderAccount(loadedAccountName);
        issueHidden.value = loadedFields.issue_date; dueHidden.value = loadedFields.due_date;
        syncDateLabel(issueBtn, issueHidden, L.pickDate); syncDateLabel(dueBtn, dueHidden, L.pickDate);
        ownerHidden.value = loadedFields.owner_id; renderOwner();
        contact = loadedContact; contactHidden.value = loadedFields.contact_id; renderContact();
        if (discountInput) { discountInput.value = Number(loadedFields.discount_amount) ? money(loadedFields.discount_amount) : ''; syncDiscountRow(); }
        applyTaxMode(loadedFields.tax_mode);
        linesApi.render(loadedLines);
        checkDirty();
    }
    [title, memo].forEach((el) => el.addEventListener('input', checkDirty));
    discardBtn.addEventListener('click', discard);
    checkDirty();

    const billToRow = $('erpBillToRow');
    // E10: 낙관적 잠금 — 저장마다 if_version 을 보내고 응답의 version 으로 갱신; 409 면 배너
    const setVersion = (v) => { if (v !== undefined && v !== null) doc.dataset.version = String(v); };
    const conflict = $('erpConflict');
    function showConflict(body) {
        if (!conflict) return;
        const who = body && body.updated_by && body.updated_by.name ? userLabel(body.updated_by) + (body.updated_by.rank ? ' ' + body.updated_by.rank : '') : '?';
        $('erpConflictText').textContent = (L.conflictBy || '%s 님이 %s 에 수정했습니다').replace('%s', who).replace('%s', (body && body.updated_at) || '');
        conflict.querySelector('[data-act="reload"]').textContent = conflict.dataset.lReload || L.reloadRecord || '다시 불러오기';
        conflict.querySelector('[data-act="overwrite"]').textContent = conflict.dataset.lOverwrite || L.overwrite || '내 변경으로 덮어쓰기';
        conflict._current = body && body.current;
        conflict.hidden = false;
    }
    function applyCurrent(d) {
        if (!d) return;
        title.value = d.title || ''; memo.value = d.memo || '';
        account.value = d.account_id || ''; renderAccount(d.account_name || (d.bill_to && d.bill_to.name) || '');
        issueHidden.value = d.issue_date || ''; dueHidden.value = d.due_date || '';
        syncDateLabel(issueBtn, issueHidden, L.pickDate); syncDateLabel(dueBtn, dueHidden, L.pickDate);
        ownerHidden.value = d.owner_id || ''; renderOwner();
        contact = d.contact || null; contactHidden.value = d.contact_id || ''; renderContact();
        if (discountInput) { discountInput.value = d.discount_amount ? money(d.discount_amount) : ''; syncDiscountRow(); }
        if (d.tax_mode) applyTaxMode(d.tax_mode);
        if (Array.isArray(d.lines)) linesApi.render(d.lines);
        $('erpSubtotal').textContent = money(d.subtotal); $('erpTax').textContent = money(d.tax); $('erpTotal').textContent = money(d.total);
        if (billToRow) renderBillTo(billToRow, d.bill_to, d.account_id);
        setVersion(d.version);
        snapshot();
    }
    if (conflict) conflict.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]'); if (!b) return;
        const cur = conflict._current; conflict.hidden = true;
        if (b.dataset.act === 'reload') { if (cur && Array.isArray(cur.lines)) applyCurrent(cur); else reloadDoc(); }
        else if (b.dataset.act === 'overwrite') { if (cur) setVersion(cur.version); save(); }
    });
    // 삭제된 문서 (관리 권한자): 복원
    const restoreBtn = $('erpRestore');
    if (restoreBtn) restoreBtn.addEventListener('click', () => {
        post('/api/erp/docs/' + doc.dataset.id + '/restore', {}, L).then(() => { loaded = serialize(); reloadDoc(); }).catch((e) => fail(L, e));
    });
    // 열 구성 (erp.cols.doc): 토글하면 저장 (기본 표시 여부는 setupERPLines 호출 시 이미 넘겼다 — §9)
    try {
        const linesRoot = $('erpLines');
        if (linesRoot && window.MutationObserver) {
            let last = linesRoot.classList.contains('show-discount');
            new MutationObserver(() => { const on = linesRoot.classList.contains('show-discount'); if (on === last) return; last = on; savePref('erp.cols.doc', on ? '[]' : '["discount"]').catch(() => { /* */ }); }).observe(linesRoot, { attributes: true, attributeFilter: ['class'] });
        }
    } catch (_) { /* */ }
    let approval = null;
    let saving = false;
    function save(extra) {
        if (saving) return Promise.resolve(false);
        const f = Object.assign(fields(), extra || {});
        if (!f.title && !linesApi.collect().length) { title.focus(); showToast(L.titleOrLines || '제목이나 품목을 입력하세요', { error: true }); return Promise.resolve(false); }
        if (locked) { delete f.lines; delete f.account_id; delete f.issue_date; delete f.title; delete f.tax_mode; delete f.discount_amount; } // 잠긴 문서: 허용 필드만 보낸다
        if (!isNew) f.if_version = doc.dataset.version || '0';
        saving = true;
        setSaveState(state, 'saving'); saveBtn.disabled = true;
        return post(isNew ? '/api/erp/docs' : '/api/erp/docs/' + doc.dataset.id + '/save', f, L).then((d) => {
            saveBtn.disabled = false;
            if (isNew) {
                loaded = serialize();
                return navigate('/erp/docs/' + d.kind + '/' + d.id, { force: true, replace: true }).then(() => {
                    const savedState = document.getElementById('erpState');
                    if (savedState) setSaveState(savedState, 'saved');
                    document.getElementById('erpTitle')?.focus();
                    return true;
                });
            }
            setVersion(d.version);
            $('erpSubtotal').textContent = money(d.subtotal); $('erpTax').textContent = money(d.tax); $('erpTotal').textContent = money(d.total);
            if (discountInput) { discountInput.value = d.discount_amount ? money(d.discount_amount) : ''; syncDiscountRow(); }
            if (d.tax_mode && d.tax_mode !== taxMode) applyTaxMode(d.tax_mode);
            if (d.contact_id !== contactHidden.value) { contact = d.contact || null; contactHidden.value = d.contact_id || ''; renderContact(); }
            doc.dataset.dealId = d.deal_id || ''; // 거래처 변경으로 서버가 끊었으면 다음 저장부터 빈 값
            if (billToRow) renderBillTo(billToRow, d.bill_to, d.account_id);
            if (d.account_name !== undefined || !d.account_id) renderAccount(d.account_name || '');
            if (payments && d.total !== payments.data().total) payments.apply(Object.assign({}, payments.data(), { total: d.total, balance: d.balance, paid_amount: d.paid_amount, effective: d.effective }));
            snapshot();
            if (approval) approval.refresh();
            setSaveState(state, 'saved');
            return true;
        }).catch((e) => {
            saveBtn.disabled = false; setSaveState(state, 'error');
            if (e && e.status === 409 && e.key === 'common.conflict') { showConflict(e.body); return false; }
            fail(L, e); return false;
        }).finally(() => { saving = false; });
    }
    saveBtn.addEventListener('click', () => { if (!isDeleted) save(); });
    const billToRefresh = $('erpBillToRefresh');
    if (billToRefresh) billToRefresh.addEventListener('click', () => save({ refresh_bill_to: '1' }));
    // 인쇄 (E4): 새 탭 …/print?auto=1 — dirty 면 저장 후 열기 (팝업 차단을 피하려고 창은 클릭 시점에 연다)
    function openPrint(auto) {
        const url = doc.dataset.printHref + (auto ? '?auto=1' : '');
        if (!dirty()) { window.open(url, '_blank', 'noopener'); return; }
        const w = window.open('', '_blank');
        appConfirm({ message: L.printUnsaved, okLabel: L.save, cancelLabel: L.cancel }).then((ok) => {
            if (!ok) { if (w) w.close(); return; }
            save().then((saved) => { if (saved && w) w.location = url; else if (w) w.close(); });
        });
    }
    function print() { if (!isNew) openPrint(true); }
    [['erpPrint', false]].forEach(([id, auto]) => { const a = $(id); if (a) a.addEventListener('click', (e) => { if (e.metaKey || e.ctrlKey || e.button !== 0) return; e.preventDefault(); openPrint(auto); }); });
    onDoc('keydown', (e) => {
        if (isDeleted) return; // 삭제된 문서: 복원 외 편집 단축키 없음
        // Ctrl+S / Ctrl+Enter = 명시적 저장 (품목 피커가 열려 있으면 Enter 는 피커 몫)
        if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 's' || (e.key === 'Enter' && !document.querySelector('.erp-item-pop')))) { e.preventDefault(); save(); return; }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && !isNew) { e.preventDefault(); print(); return; }
        if (e.ctrlKey || e.metaKey || e.altKey || inField()) return;
        if (e.key === 'o' || e.key === 'O') { e.preventDefault(); openOwner(); }
        else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); setOwner(peopleIndex().me || doc.dataset.me); }
    });
    onWin('beforeunload', (e) => { if (dirty()) { e.preventDefault(); e.returnValue = ''; } });
    // 섹션 안 SPA 이동도 같은 dirty 가드: 확인 뒤 force 로 다시 이동한다
    onDoc('calendar:before-navigate', (e) => {
        if (!dirty()) return;
        e.preventDefault();
        appConfirm({ message: L.unsaved || L.printUnsaved || '', okLabel: L.discardChanges || L.cancel, cancelLabel: L.cancel, danger: true }).then((ok) => { if (ok) { loaded = serialize(); navigate(e.detail.href, { force: true }); } });
    });

    // 헤더 상태 필 = 파생값 (부분입금 12,000,000 / 34,000,000)
    const pill = $('erpStatusPill');
    const flowEl = $('erpFlow'), nextEl = $('erpNextSteps'), more = $('erpMore'), numberEl = $('erpNumber');
    // C-3 §6.1: 1행 = 저장된 문서 상태, 2행 = 파생 입금 상태(청구 확정된 청구서만).
    const payPill = $('erpPayPill');
    function setPill(effective, label, paid, total) {
        const stored = doc.dataset.status || effective;
        pill.className = 'erp-status st-' + stored;
        pill.textContent = transitions.labels[stored] || label || stored;
        if (!payPill) return;
        const derived = ['partial', 'paid', 'overdue'].includes(effective) ? effective
            : (stored === 'issued' && doc.dataset.kind === 'invoice' ? 'issued' : '');
        payPill.hidden = !derived;
        doc.dataset.effective = effective;
        if (!derived) return;
        payPill.className = 'erp-status erp-pay-status st-' + derived;
        payPill.textContent = payPill.dataset['l' + derived.charAt(0).toUpperCase() + derived.slice(1)] || derived;
        if (derived === 'partial') {
            const s = document.createElement('small');
            s.className = 'erp-status-amt';
            s.textContent = money(paid) + ' / ' + money(total);
            payPill.appendChild(document.createTextNode(' '));
            payPill.appendChild(s);
        }
    }
    // 입금 카드 (E5, 청구서 · 초안 제외)
    const payCard = $('erpPayments');
    let payments = null;
    if (payCard) {
        let seed = null; try { seed = JSON.parse(doc.dataset.payments || 'null'); } catch (_) { seed = null; }
        payments = setupERPPayments(payCard, { docId: doc.dataset.id, seed, L, money, num, canPay, today: doc.dataset.today, post, onChange: (d) => {
            const row = $('erpBalanceRow'); if (!row) return;
            row.classList.toggle('due', d.balance > 0); row.classList.toggle('ok', !(d.balance > 0));
            row.innerHTML = '';
            const lab = document.createElement('span'), val = document.createElement('b'); val.id = 'erpBalance';
            if (d.balance > 0) { lab.textContent = row.dataset.lBalance; val.textContent = money(d.balance); } else { lab.textContent = row.dataset.lPaidOn; val.className = 'mono'; val.textContent = d.paid_at || ''; }
            row.appendChild(lab); row.appendChild(val);
            if (d.effective) { setPill(d.effective, null, d.paid_amount, d.total); renderFlow({ status: doc.dataset.status, effective: d.effective }); renderNext({ status: doc.dataset.status, balance: d.balance, paid_amount: d.paid_amount, next_action: d.next_action }); } // 입금·삭제·복원 응답의 next_action (입금 기록 ↔ 입금 완료)
            if (d.version !== undefined) doc.dataset.version = String(d.version);
        } });
    }
    // 세금계산서 카드 (C-3 §6.2, 청구서 · 청구 확정 후)
    const taxCard = $('erpTaxCard');
    if (taxCard) setupTaxInvoiceCard(taxCard, { post, L, docId: doc.dataset.id, version: () => doc.dataset.version || '0', onSaved: setVersion });
    if (isNew) return;
    approval = setupERPApproval(doc, { L, post, dirty, onNext: (na) => renderNext({ next_action: na }) });
    // A5: header chip → 접힌 승인 카드 펼치기/접기. 카드 자체는 승인 기능이
    // 연결되어 있기만 하면 항상 렌더된다 (ApprovalAvailable) — 정책이 꺼져
    // 있고 아직 요청도 없으면 칩만 없을 뿐, 카드는 숨겨진 채로 존재한다.
    // 그럴 때는 ⋯ 메뉴의 "승인 요청…" 항목이 같은 revealApproval 을 통해
    // 카드를 펼친다 (임의 승인 요청 경로, erp.md §3 item 1).
    const approvalChip = $('erpApprovalChip'), approvalCard = $('erpApproval');
    function revealApproval() {
        if (!approvalCard) return;
        approvalCard.hidden = false;
        if (approvalChip) approvalChip.setAttribute('aria-expanded', 'true');
        approvalCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    if (approvalChip && approvalCard) approvalChip.addEventListener('click', () => {
        approvalCard.hidden = !approvalCard.hidden;
        approvalChip.setAttribute('aria-expanded', String(!approvalCard.hidden));
        if (!approvalCard.hidden) approvalCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    if (location.hash === '#erpApproval') revealApproval(); // 승인함 행 → 문서: 카드가 열린 채로 착지 (§9.3)

    // ── E6: 흐름 스테퍼 · 다음 단계 · 잠금 · 이력 ──
    const isLocked = (status) => (LOCKED[kind] || []).includes(status);
    function renderFlow(d) {
        if (!flowEl) return;
        const flow = transitions.flow || [];
        const eff = d.effective || d.status;
        let pos = flow.indexOf(d.status);
        if (kind === 'invoice' && d.status === 'issued') pos = eff === 'paid' ? 2 : 1;
        flowEl.innerHTML = '';
        flow.forEach((k, i) => {
            const s = document.createElement('span'); s.className = 'erp-step' + (pos >= 0 && i < pos ? ' done' : '') + (i === pos ? ' current' : ''); s.dataset.step = k; s.setAttribute('role', 'listitem');
            const n = document.createElement('i'); n.textContent = pos >= 0 && i < pos ? '✓' : String(i + 1);
            s.appendChild(n); s.appendChild(document.createTextNode(transitions.labels[k] || k));
            flowEl.appendChild(s);
        });
        let side = ['rejected', 'expired', 'cancelled'].includes(d.status) ? d.status : (['overdue', 'partial'].includes(eff) ? eff : '');
        if (side) { const s = document.createElement('span'); s.className = 'erp-step side erp-status st-' + side; s.dataset.step = side; s.textContent = transitions.labels[side] || side; flowEl.appendChild(s); }
        let reason = doc.querySelector('.erp-cancel-reason');
        if (d.cancel_reason) {
            if (!reason) { reason = document.createElement('span'); reason.className = 'erp-cancel-reason'; reason.title = L.cancelReason || '사유'; pill.insertAdjacentElement('afterend', reason); }
            reason.textContent = '“' + d.cancel_reason + '”';
        } else if (reason && d.cancel_reason === '') reason.remove();
    }
    function nextKeys(status) { return (transitions.table[status] || []).slice(); }
    // 다섯 모듈 UX §9.1: 머리의 주 행동은 서버(models.ERPNextAction)가 정한 값 하나다 — 첫 렌더는
    // 템플릿이, 그 뒤는 상태·입금·전환·결재 응답의 next_action 으로 다시 그린다. 값이 없는 응답은
    // 지금 그림을 지킨다. 나머지 전이는 ⋯ 메뉴에 있다.
    let nextAction = null;
    try { nextAction = JSON.parse(doc.dataset.nextAction || 'null'); } catch (_) { nextAction = null; }
    function renderNext(d) {
        if (!nextEl) return;
        const na = d && d.next_action;
        if (!na) return;
        nextAction = na;
        nextEl.innerHTML = '';
        if (na.kind === 'none') {
            if (na.label) { const s = document.createElement('span'); s.className = 'erp-next-state'; s.textContent = na.label; nextEl.appendChild(s); }
        } else if (na.kind === 'link') {
            const a = document.createElement('a'); a.className = 'btn btn-sm btn-primary erp-next-btn'; a.id = 'erpNextBtn'; a.dataset.next = 'link'; a.dataset.target = na.target || ''; a.href = na.href || '#'; a.textContent = na.label || ''; nextEl.appendChild(a);
        } else {
            const b = document.createElement('button'); b.type = 'button'; b.className = 'btn btn-sm btn-primary erp-next-btn'; b.id = 'erpNextBtn'; b.dataset.next = na.kind; b.dataset.target = na.target || '';
            if (na.kind === 'status') b.dataset.status = na.target || '';
            if (na.why) b.disabled = true;
            b.textContent = na.label || ''; nextEl.appendChild(b);
        }
        if (na.why) { const p = document.createElement('p'); p.className = 'erp-next-why'; p.textContent = na.why; nextEl.appendChild(p); }
        syncMoreDupes();
    }
    // 주 행동과 같은 보조 버튼(전환 · 거래명세서 만들기 · 자식 열기)은 숨긴다 (스펙 §9.1)
    function syncMoreDupes() {
        const na = nextAction || {};
        const conv = $('erpConvert'); if (conv && !nextEl.contains(conv)) conv.hidden = na.kind === 'convert' && conv.dataset.to === na.target;
        const st = $('erpStatementNew'); if (st) st.hidden = na.kind === 'statement';
        const child = $('erpChildLink'); if (child) child.hidden = na.kind === 'link';
    }
    function isPrimaryStatus(k) { return !!nextAction && nextAction.kind === 'status' && nextAction.target === k; }
    // 「입금 기록」: 입금 카드로 내려가 퀵 행의 금액 칸에 포커스
    function focusPayment() {
        if (!payCard) return;
        payCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const amt = payCard.querySelector('.erp-pay-quick .erp-pay-amount'); if (amt) { amt.focus(); amt.select(); }
    }
    // 「복사해서 새 문서」 = ⋯ 의 복제와 같은 API
    function duplicateDoc() {
        post('/api/erp/docs/' + doc.dataset.id + '/duplicate', {}, L).then((d) => navigate('/erp/docs/' + d.kind + '/' + d.id)).catch((e) => fail(L, e));
    }
    function setLockedUI(on) {
        locked = on; doc.dataset.locked = on ? '1' : '';
        title.readOnly = on; account.disabled = on; if (accountBtn) accountBtn.disabled = on; issueBtn.disabled = on;
        if (taxSeg) taxSeg.querySelectorAll('[data-mode]').forEach((b) => { b.disabled = on; });
        if (discountInput) discountInput.readOnly = on;
        if (discountAdd) discountAdd.hidden = on;
        if (billToRefresh) billToRefresh.hidden = on;
        linesApi.setLocked(on);
        doc.querySelector('.erp-card-lines').classList.toggle('locked', on);
        const hint = doc.querySelector('.erp-lines-hint'); if (hint) hint.textContent = on ? (L.lockedHint || '') + ' · ' + (L.lockedReadonly || '읽기 전용') : (hint.dataset.l || hint.textContent);
        let lp = $('erpLockPill');
        if (on && !lp) { lp = document.createElement('span'); lp.className = 'erp-lock-pill'; lp.id = 'erpLockPill'; lp.title = L.lockedHint || ''; lp.textContent = '🔒 ' + (L.lockedHint || ''); pill.insertAdjacentElement('afterend', lp); }
        if (!on && lp) lp.remove();
        canUnlock = on && (canPay || canManage) && doc.dataset.status !== 'cancelled';
        doc.dataset.canUnlock = canUnlock ? '1' : '';
        const ub = $('erpUnlock'); if (ub) ub.hidden = !canUnlock;
        snapshot();
    }
    const hintEl = doc.querySelector('.erp-lines-hint'); if (hintEl && !locked) hintEl.dataset.l = hintEl.textContent;
    // 이력 카드
    const histList = $('erpHistoryList'), histCount = $('erpHistoryCount');
    function prependEvent(ev) {
        if (!histList || !ev) return;
        const row = document.createElement('div'); row.className = 'erp-event k-' + ev.kind; row.dataset.id = ev.id || '';
        if (ev.user && ev.user.id) row.appendChild(personEl(ev.user, 'xs'));
        else { const s = document.createElement('span'); s.className = 'person xs system'; const i = document.createElement('i'); i.className = 'rec-avatar'; i.textContent = '⚙'; const b = document.createElement('b'); b.className = 'person-name'; b.textContent = $('erpHistory').dataset.lSystem || '시스템'; s.appendChild(i); s.appendChild(b); row.appendChild(s); }
        const lab = document.createElement('span'); lab.className = 'erp-event-label'; lab.textContent = ev.label || ''; row.appendChild(lab);
        const vals = document.createElement('span'); vals.className = 'erp-event-vals';
        if (ev.from_label) { const f = document.createElement('span'); f.className = 'erp-event-from'; f.textContent = ev.from_label; vals.appendChild(f); vals.appendChild(document.createTextNode(' → ')); }
        if (ev.to_label) { const t = document.createElement('b'); t.textContent = ev.to_label; vals.appendChild(t); }
        row.appendChild(vals);
        if (ev.note) { const n = document.createElement('span'); n.className = 'erp-event-note'; n.textContent = '“' + ev.note + '”'; row.appendChild(n); }
        const at = document.createElement('span'); at.className = 'erp-event-at mono'; at.textContent = ev.at || ''; row.appendChild(at);
        histList.insertBefore(row, histList.firstChild);
        const empty = histList.querySelector('.erp-history-empty'); if (empty) empty.hidden = true;
        if (histCount) histCount.textContent = String(histList.querySelectorAll('.erp-event').length);
    }
    function localEvent(kind2, from, to) {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return { kind: kind2, label: L['event' + kind2.charAt(0).toUpperCase() + kind2.slice(1)] || kind2, from_label: from, to_label: to, user: personOf(peopleIndex().me || doc.dataset.me), at: now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()) + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes()) };
    }
    // 상태 적용 (응답 문서 JSON)
    function applyStatus(d) {
        const from = doc.dataset.status;
        doc.dataset.status = d.status;
        if (d.version !== undefined) doc.dataset.version = String(d.version);
        setPill(d.effective || d.status, null, d.paid_amount, d.total);
        renderFlow(d); renderNext(d);
        const conv = $('erpConvert');
        if (conv) {
            conv.disabled = ['rejected', 'expired', 'cancelled', 'draft'].includes(d.status);
            conv.title = conv.disabled ? L.convertNotAllowed : '';
        }
        if (d.event) prependEvent(d.event);
        if (isLocked(d.status) !== locked) setLockedUI(isLocked(d.status));
        setSaveState(state, 'saved');
        // 청구서의 초안↔발행은 입금 카드가 생기고 사라지므로 다시 읽는다
        if (kind === 'invoice' && (from === 'draft' || d.status === 'draft') && !dirty()) reloadDoc();
    }
    function setStatus(status, note) {
        return post('/api/erp/docs/' + doc.dataset.id + '/status', note ? { status, note } : { status }, L).then(applyStatus).catch((err) => fail(L, err));
    }
    if (nextEl) nextEl.addEventListener('click', (e) => {
        const b = e.target.closest('[data-next]'); if (!b || b.disabled) return;
        switch (b.dataset.next) {
            case 'status': setStatus(b.dataset.target, ''); break;
            case 'convert': convert(b.dataset.target, b); break;
            case 'statement': startStatement(b); break;
            case 'payment': focusPayment(); break;
            case 'copy': duplicateDoc(); break;
            default: break; // link: 앵커 그대로 (SPA 가 가로챈다)
        }
    });
    // 잠금 해제 (번호 우클릭, erp.pay | erp.manage)
    function unlock() {
        appConfirm({ message: L.unlockConfirm, okLabel: L.unlock, cancelLabel: L.cancel }).then((ok) => {
            if (!ok) return;
            post('/api/erp/docs/' + doc.dataset.id + '/unlock', {}, L).then((d) => {
                hasUnlock = true; doc.dataset.hasUnlock = '1';
                if (kind === 'invoice') { loaded = serialize(); reloadDoc(); return; }
                applyStatus(d);
                showToast((L.unlock || '잠금 해제') + ' · ' + doc.dataset.number, {});
            }).catch((err) => fail(L, err));
        });
    }
    const unlockBtn = $('erpUnlock'); if (unlockBtn) unlockBtn.addEventListener('click', unlock);
    if (numberEl) numberEl.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        const menu = doc.dataset.printable ? [{ label: L.print || '인쇄', onClick: print, hint: 'Ctrl+P' }] : [];
        if (canUnlock) menu.unshift({ label: '🔓 ' + (L.unlock || '잠금 해제'), onClick: unlock }, { separator: true });
        if (!menu.length) return; // 수주 + 잠금 해제 불가 → 빈 메뉴를 띄우지 않는다
        showContextMenu(e.clientX, e.clientY, menu);
    });
    // 전환 / 삭제 (+ ⋯)
    function childLabel(to) { return to === 'order' ? (L.kindOrder || '수주') : to === 'statement' ? (L.kindStatement || '거래명세서') : (L.kindInvoice || '청구서'); }
    // 연결 문서 줄에 자식 칩 하나를 붙인다 (showChildLink 와 doConvert('statement') 공용)
    function appendChildChip(d) {
        const strip = $('erpChildren'); if (!strip) return;
        strip.hidden = false;
        const link = document.createElement('a'); link.className = 'erp-link'; link.href = '/erp/docs/' + d.kind + '/' + d.id; link.textContent = childLabel(d.kind) + ' ' + d.number + ' ';
        const st = document.createElement('span'); st.className = 'erp-status st-' + (d.effective || d.status); st.textContent = transitions.labels[d.effective || d.status] || d.status; link.appendChild(st);
        strip.appendChild(document.createTextNode(' ')); strip.appendChild(link);
    }
    function showChildLink(d) {
        const conv = $('erpConvert');
        if (conv && !nextEl.contains(conv)) {
            const a = document.createElement('a'); a.className = 'btn btn-secondary btn-sm erp-child-link'; a.id = 'erpChildLink'; a.href = '/erp/docs/' + d.kind + '/' + d.id;
            a.textContent = childLabel(d.kind) + ' ' + d.number + ' ' + (L.openChild || '열기') + ' →';
            conv.replaceWith(a);
        }
        appendChildChip(d);
    }
    function doConvert(to, extra) {
        return post('/api/erp/docs/' + doc.dataset.id + '/convert', Object.assign({ to }, extra || {}), L).then((d) => {
            const href = '/erp/docs/' + d.kind + '/' + d.id;
            if (d.kind === 'statement') appendChildChip(d); // 전환 버튼은 그대로 둔다
            else showChildLink(d);
            if (kind === 'order' && d.kind === 'invoice') { // 수주에서 곧바로 청구서가 나갔다 — 명세서 길은 닫힌다 (erp.order_already_invoiced)
                const st = $('erpStatementNew'); if (st) { st.disabled = true; st.title = (L.errors && L.errors['erp.order_already_invoiced']) || ''; }
            }
            prependEvent(localEvent('convert', doc.dataset.number, d.number));
            if (d.parent_version !== undefined) doc.dataset.version = String(d.parent_version);
            if (d.parent_status && d.parent_status !== doc.dataset.status) {
                prependEvent(localEvent('status', transitions.labels[doc.dataset.status], transitions.labels[d.parent_status]));
                applyStatus({ status: d.parent_status, effective: d.parent_status, paid_amount: 0, total: 0 });
            }
            if (d.parent_next_action) renderNext({ next_action: d.parent_next_action }); // 자식이 생긴 부모의 주 행동 = 그 자식 열기
            const toast = showToast(childLabel(d.kind) + ' ' + d.number + ' ' + (L.convertedToast || '만들었습니다'), { actionLabel: L.openChild || '열기', onAction: () => navigate(href), duration: 12000 });
            if (d.suggest_won && d.suggest_won.deal_id) {
                const b = document.createElement('button'); b.type = 'button'; b.textContent = L.markDealWon || '영업 기회를 수주로 표시';
                b.addEventListener('click', () => {
                    b.disabled = true;
                    post('/api/crm/deals/' + encodeURIComponent(d.suggest_won.deal_id) + '/patch', { stage: 'won', won_amount: String(d.suggest_won.amount || 0) }, L)
                        .then(() => showToast(L.dealWonDone || '', {})).catch((err) => fail(L, err));
                });
                toast.appendChild(b);
            }
            return true; // M1: caller (openStatementDialog) needs to know the convert actually succeeded
        }).catch((err) => {
            if (err && /already_converted|이미 전환/.test(err.message)) reloadDoc(); else fail(L, err);
            return false;
        });
    }
    // C-2 §5: 거래명세서 만들기 — 수주 줄의 잔여 수량을 기본값으로 채운 표.
    function openStatementDialog(anchor) {
        const old = document.querySelector('.erp-stmt-pop'); if (old) old.remove();
        fetch('/api/erp/docs/' + doc.dataset.id + '/statement', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(async (r) => {
                const body = await r.json().catch(() => ({})); // L2: a non-2xx body still parses as JSON ({"message": key})
                if (!r.ok) { const err = new Error((L.errors && L.errors[body.message]) || body.message || ('HTTP ' + r.status)); err.status = r.status; throw err; }
                return body;
            }).then((data) => {
                const rows = (data && data.lines) || [];
                if (!rows.some((l) => l.remaining > 0)) { showToast(L.statementNothing || '남은 수량이 없습니다', { error: true }); return; }
                const pop = document.createElement('div'); pop.className = 'prop-pop erp-stmt-pop'; pop.setAttribute('role', 'dialog');
                const h = document.createElement('div'); h.className = 'erp-reason-title'; h.textContent = L.statementTitle || '거래명세서 만들기'; pop.appendChild(h);
                const hint = document.createElement('p'); hint.className = 'form-hint'; hint.textContent = L.statementHint || '이번에 납품한 수량만 남기세요. 기본값은 잔여 수량입니다.'; pop.appendChild(hint);
                const table = document.createElement('div'); table.className = 'erp-stmt-rows'; pop.appendChild(table);
                rows.forEach((l) => {
                    const r = document.createElement('div'); r.className = 'erp-stmt-row'; r.dataset.line = l.line_id;
                    const nm = document.createElement('span'); nm.className = 'erp-stmt-name'; nm.textContent = l.name + (l.spec ? ' · ' + l.spec : ''); r.appendChild(nm);
                    const rem = document.createElement('span'); rem.className = 'erp-stmt-rem'; rem.textContent = (L.statementRemaining || '잔여') + ' ' + l.remaining + (l.unit || ''); r.appendChild(rem);
                    const inp = document.createElement('input'); inp.className = 'form-input num erp-stmt-qty'; inp.inputMode = 'decimal'; inp.value = l.remaining; inp.max = String(l.remaining); inp.disabled = !(l.remaining > 0); r.appendChild(inp);
                    table.appendChild(r);
                });
                const act = document.createElement('div'); act.className = 'erp-reason-actions';
                const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'btn btn-secondary btn-sm'; cancel.textContent = L.cancel || '취소';
                const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-primary btn-sm'; ok.id = 'erpStatementCreate'; ok.textContent = L.statementCreate || '만들기';
                act.appendChild(cancel); act.appendChild(ok); pop.appendChild(act);
                document.body.appendChild(pop);
                const rect = anchor.getBoundingClientRect();
                pop.style.left = Math.max(8, Math.min(rect.left, window.innerWidth - pop.offsetWidth - 8)) + 'px';
                pop.style.top = Math.min(rect.bottom + 4, window.innerHeight - pop.offsetHeight - 8) + 'px';
                const close = () => { pop.remove(); document.removeEventListener('mousedown', outside, true); };
                const outside = (e) => { if (!pop.contains(e.target)) close(); };
                setTimeout(() => onDoc('mousedown', outside, true), 0);
                cancel.addEventListener('click', close);
                pop.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } });
                const first = pop.querySelector('.erp-stmt-qty:not([disabled])'); if (first) { first.focus(); first.select(); }
                ok.addEventListener('click', () => {
                    const sel = Array.from(pop.querySelectorAll('.erp-stmt-row')).map((r) => ({ line_id: r.dataset.line, qty: num(r.querySelector('.erp-stmt-qty').value) })).filter((x) => x.qty > 0);
                    if (!sel.length) { showToast(L.statementNothing || '', { error: true }); return; }
                    ok.disabled = true;
                    // M1: doConvert never rejects (it shows the error itself and resolves
                    // false) — only close the dialog on an actual success, otherwise leave
                    // the typed quantities in place and let the user retry.
                    doConvert('statement', { lines: JSON.stringify(sel) }).then((okDone) => { if (okDone) close(); else ok.disabled = false; });
                });
            }).catch((e) => fail(L, e));
    }
    function startStatement(anchor) {
        if (dirty()) { appConfirm({ message: L.saveThenConvert, okLabel: L.save, cancelLabel: L.cancel }).then((okd) => { if (okd) save().then((saved) => { if (saved) openStatementDialog(anchor); }); }); return; }
        openStatementDialog(anchor);
    }
    const stmtBtn = $('erpStatementNew');
    if (stmtBtn) stmtBtn.addEventListener('click', () => { if (!stmtBtn.disabled) startStatement(stmtBtn); });
    function convert(to, btn) {
        const conv = btn || $('erpConvert'); if (!conv || conv.disabled) return;
        to = to || conv.dataset.to;
        if (dirty()) {
            appConfirm({ message: L.saveThenConvert, okLabel: L.save, cancelLabel: L.cancel }).then((ok) => { if (ok) save().then((saved) => { if (saved) doConvert(to); }); });
            return;
        }
        appConfirm({ message: to === 'order' ? L.convertOrder : L.convertInvoice, okLabel: conv.textContent.replace('→', '').trim(), cancelLabel: L.cancel }).then((ok) => { if (ok) doConvert(to); });
    }
    function remove() {
        const children = doc.dataset.children || '';
        appConfirm({ message: L.deleteConfirm, detail: children ? (L.deleteWithChildren || '') + ' ' + children : '', okLabel: L.delete, cancelLabel: L.cancel, danger: true }).then((ok) => {
            if (!ok) return;
            post('/api/erp/docs/' + doc.dataset.id + '/delete', {}, L).then(() => { loaded = serialize(); navigate('/erp/docs/' + kind, { force: true }); }).catch((err) => fail(L, err));
        });
    }
    doc.addEventListener('click', (e) => { const conv = e.target.closest('#erpConvert'); if (conv) convert(conv.dataset.to, conv); });
    if (more) more.addEventListener('click', (e) => {
        const r = more.getBoundingClientRect();
        const menu = [];
        const conv = $('erpConvert'), child = $('erpChildLink');
        if (conv && !conv.disabled && !conv.hidden) menu.push({ label: conv.textContent.trim(), onClick: () => convert(conv.dataset.to, conv) });
        if (child && !child.hidden) menu.push({ label: child.textContent.trim(), onClick: () => navigate(child.href) });
        // 다섯 모듈 UX §9.5 (원칙 12): ≤900px 에서 .erp-doc-more 가 숨으면 그 버튼들(거래명세서 만들기 ·
        // 업무 요청 · 인쇄/PDF · 보내기)은 ⋯ 로 온다 — 데스크톱에서는 버튼이 보이므로 중복하지 않는다.
        const moreArea = doc.querySelector('.erp-doc-more');
        if (moreArea && getComputedStyle(moreArea).display === 'none') {
            const before = menu.length;
            const stmt = $('erpStatementNew'); if (stmt && !stmt.disabled && !stmt.hidden) menu.push({ label: stmt.textContent.trim(), onClick: () => startStatement(more) });
            const work = moreArea.querySelector('a[href^="/work/requests/new"]'); if (work) menu.push({ label: work.textContent.trim(), onClick: () => { window.location.href = work.href; } });
            if (doc.dataset.printable) menu.push({ label: L.printPdf || L.print || '인쇄/PDF', onClick: print });
            if ($('erpSend')) menu.push({ label: (L.send || '보내기') + '…', onClick: () => { if (window.__erpOpenSend) window.__erpOpenSend(); } });
            if (menu.length > before) menu.push({ separator: true });
        }
        nextKeys(doc.dataset.status).forEach((k) => {
            if (isPrimaryStatus(k)) return; // 주 버튼이 이미 보인다
            if (NEEDS_NOTE[k]) {
                if (kind === 'invoice' && k === 'cancelled' && payments && payments.data().paid_amount > 0) return;
                menu.push({ label: transitions.labels[k] || k, danger: true, onClick: () => reasonPop(more, L, { title: (transitions.labels[k] || k) + ' · ' + (L.cancelReason || '사유'), okLabel: transitions.labels[k] || k, danger: true, onSubmit: (v) => setStatus(k, v) }) });
            } else {
                menu.push({ label: transitions.labels[k] || k, onClick: () => setStatus(k, '') });
            }
        });
        if (payments && (payments.data().balance > 0) && canPay && doc.dataset.status === 'issued') menu.push({ label: L.payFull || '전액 입금', onClick: () => { const q = payCard.querySelector('.erp-pay-quick'); if (q) q._submit(true); } });
        if (canUnlock) menu.push({ label: '🔓 ' + (L.unlock || '잠금 해제'), onClick: unlock });
        // 정책 off + 요청 없음 → 칩이 없어도 (렌더는 되어 있는) 카드로 임의 승인
        // 요청을 시작할 길을 남긴다. 칩이 있으면 이미 눈에 보이므로 중복 항목을
        // 넣지 않는다.
        if (approvalCard && !approvalChip) menu.push({ label: (L.approvalRequest || '승인 요청') + '…', onClick: revealApproval });
        if (!isDeleted) menu.push({ separator: true }, { label: L.duplicate || L.menuDuplicate || '복제', onClick: duplicateDoc });
        if (canManage) {
            const deletable = !!doc.dataset.deletable && !hasUnlock;
            // "대신 취소하세요" 는 이 kind 에 취소로 갈 수 있는 전이가 있을 때만 의미가
            // 있다 (예: 견적은 발송 후 거절/승인만 있고 취소가 없다 — erp.md §3-9).
            const hasCancelPath = Object.values(transitions.table || {}).some((tos) => (tos || []).includes('cancelled'));
            const hint = deletable ? '' : ' — ' + (hasCancelPath
                ? (hasUnlock ? (L.deleteWasIssued || '') : (L.deleteNotDraft || ''))
                : (hasUnlock ? (more.dataset.lDeleteWasIssuedShort || '') : (more.dataset.lDeleteNotDraftShort || '')));
            menu.push({ separator: true }, { label: (L.delete || '삭제') + hint, danger: true, disabled: !deletable, onClick: remove });
        }
        showContextMenu(r.left, r.bottom + 4, menu);
        e.stopPropagation();
    });
    // 헤더 빈 곳 우클릭도 ⋯ 와 같은 메뉴
    const head = doc.querySelector('.erp-doc-head');
    if (head && more) head.addEventListener('contextmenu', (e) => { if (e.target.closest('button, a, input, #erpNumber')) return; e.preventDefault(); more.click(); });
    // C-1 배송 계층(공유·첨부·보내기)은 문서 편집기에서만 필요하다 — 지연 로드
    if ($('erpShare') || $('erpFiles') || $('erpSend')) import('./erp-send.js').then((m) => m.setup(L));
}

// ── 문서 목록 (E8/E10): 선택바 · 상태 필 피커 · 담당 셀 피커 · 우클릭 · 키보드 · 거래처 칩 · 범위 칩 · 저장된 보기 · 열 숨기기 ──
function refreshList() { return navigate(location.pathname + location.search, { replace: true, keepScroll: true }); }
function hasSpaRoot() { return !!document.getElementById('recList'); }
// 범위 칩: 선택을 /api/prefs erp.scope 에 저장한 뒤 이동 (목록은 SPA, 대시보드는 전체 로드)
function setupScopeChips() {
    onDoc('click', (e) => {
        const a = e.target.closest('a.erp-scope-chip'); if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
        e.preventDefault(); e.stopPropagation();
        const href = a.getAttribute('href');
        savePref('erp.scope', a.dataset.scope || '').catch(() => { /* 저장 실패해도 이동 */ }).finally(() => { if (hasSpaRoot()) navigate(href); else window.location.href = href; });
    }, true);
}
function fmt(pattern, n) { return String(pattern || '%d').replace('%d', String(n)); }
function withParam(k, v) {
    const u = new URL(window.location.href);
    if (v) u.searchParams.set(k, v); else u.searchParams.delete(k);
    return u.pathname + u.search;
}
// 거래처 피커: /api/crm/accounts?q= 검색. 목록 필터(첫 행 = 전체 거래처)와 편집기(opts.allowNone = 없음 행,
// opts.allowNew = 「＋ 새 거래처」 → 이름 · 사업자등록번호 두 칸 → POST /api/erp/accounts, 스펙 §9.2)가 같이 쓴다.
function openAccountPicker(anchor, L, current, onPick, opts) {
    const o = Object.assign({ allowNone: false, allowNew: false }, opts || {});
    const old = document.querySelector('.erp-account-pop'); if (old) old.remove();
    const pop = document.createElement('div'); pop.className = 'prop-pop erp-account-pop'; pop.setAttribute('role', 'listbox');
    const search = document.createElement('input'); search.type = 'text'; search.className = 'form-input'; search.placeholder = L.searchAccount || '거래처 검색'; search.autocomplete = 'off';
    const listEl = document.createElement('div'); listEl.className = 'prop-pop-list erp-account-list';
    pop.appendChild(search); pop.appendChild(listEl);
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - 300)) + 'px';
    pop.style.top = Math.min(r.bottom + 4, window.innerHeight - 320) + 'px';
    let rows = [], sel = 0, timer = null, seq = 0, creating = false;
    const close = () => { pop.remove(); document.removeEventListener('mousedown', outside, true); };
    const outside = (e) => { if (!pop.contains(e.target) && e.target !== anchor) close(); };
    setTimeout(() => onDoc('mousedown', outside, true), 0);
    function pick(a) { if (!a) return; if (a.create) { showCreate(a.query); return; } close(); onPick(a); }
    function render() {
        listEl.innerHTML = '';
        rows.forEach((a, i) => {
            const b = document.createElement('button'); b.type = 'button'; b.className = 'prop-pop-item erp-account-row' + (i === sel ? ' sel' : '') + (a.id && a.id === current ? ' current' : '') + (a.id ? '' : (a.create ? ' new' : ' all'));
            b.textContent = a.name; b.addEventListener('click', () => pick(a));
            b.addEventListener('mousemove', () => { if (sel !== i) { sel = i; render(); } });
            listEl.appendChild(b);
        });
        if (!rows.length) { const e = document.createElement('div'); e.className = 'erp-item-pop-hint'; e.textContent = '—'; listEl.appendChild(e); }
    }
    function load() {
        const my = ++seq;
        const q = search.value.trim();
        fetch('/api/crm/accounts?q=' + encodeURIComponent(q), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then((res) => (res.ok ? res.json() : [])).then((arr) => {
                if (my !== seq) return;
                const first = o.allowNone ? { id: '', name: '— ' + (L.none || '없음') } : { id: '', name: L.accountFilterAll || '전체 거래처' };
                rows = [first].concat((arr || []).slice(0, 50));
                if (o.allowNew) rows.push({ id: '', create: true, query: q, name: (L.newAccount || '＋ 새 거래처') + (q ? ' “' + q + '”' : '') });
                sel = Math.min(sel, rows.length - 1); render();
            }).catch(() => { rows = []; render(); });
    }
    // 「＋ 새 거래처」: 이름 · 사업자등록번호 두 칸만 → POST /api/erp/accounts → 만든 거래처를 고른다 (영업 패널의 인라인 생성과 같은 규칙)
    function showCreate(query) {
        search.hidden = true; listEl.innerHTML = '';
        const form = document.createElement('div'); form.className = 'erp-account-form';
        const name = document.createElement('input'); name.type = 'text'; name.className = 'form-input erp-account-new-name'; name.placeholder = L.accountName || '거래처 이름'; name.maxLength = 200; name.autocomplete = 'off'; name.value = query || '';
        const biz = document.createElement('input'); biz.type = 'text'; biz.className = 'form-input mono erp-account-new-bizno'; biz.placeholder = L.accountBizNo || '사업자등록번호 (선택)'; biz.inputMode = 'numeric'; biz.maxLength = 12; biz.autocomplete = 'off';
        const act = document.createElement('div'); act.className = 'erp-reason-actions';
        const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'btn btn-secondary btn-sm'; cancel.textContent = L.cancel || '취소';
        const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-primary btn-sm erp-account-new-ok'; ok.textContent = L.create || '만들기';
        act.appendChild(cancel); act.appendChild(ok);
        form.appendChild(name); form.appendChild(biz); form.appendChild(act); listEl.appendChild(form);
        const back = () => { form.remove(); search.hidden = false; render(); search.focus(); };
        const submit = () => {
            if (creating) return;
            const n = name.value.trim(); if (!n) { name.focus(); return; }
            creating = true; ok.disabled = true;
            post('/api/erp/accounts', { name: n, biz_no: biz.value.trim() }, L).then((a) => {
                close(); showToast(L.accountCreated || '거래처를 만들었습니다', {}); onPick({ id: a.id, name: a.name });
            }).catch((e) => { creating = false; ok.disabled = false; fail(L, e); name.focus(); });
        };
        cancel.addEventListener('click', back); ok.addEventListener('click', submit);
        form.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); back(); }
        });
        name.focus(); name.select();
    }
    search.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(load, 120); });
    search.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); sel = Math.min(rows.length - 1, sel + 1); render(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); sel = Math.max(0, sel - 1); render(); }
        else if (e.key === 'Enter') { e.preventDefault(); pick(rows[sel]); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    });
    load(); search.focus();
}

// 선택바(화면 하단 고정)에서 여는 피커/팝오버는 위로 열리도록 가짜 앵커를 위쪽에 둔다
function aboveAnchor(el, height) {
    const r = el.getBoundingClientRect();
    const a = document.createElement('span'); a.className = 'erp-above-anchor'; a.style.cssText = 'position:fixed;width:0;height:0;left:' + r.left + 'px;top:' + Math.max(8, r.top - (height || 380)) + 'px;';
    document.body.appendChild(a);
    setTimeout(() => a.remove(), 1000);
    return a;
}
function setupDocList(list, L) {
    const kind = list.dataset.docKind || '';
    const canPay = !!list.dataset.canPay, canManage = !!list.dataset.canManage;
    // I5: 고객에게 나가는 종류만 인쇄를 건다 (models.ERPPrintableKinds — 수주는 내부 문서라 /print 가 404 다)
    const printable = !!list.dataset.printable;
    let transitions = { table: {}, labels: {} };
    try { transitions = Object.assign(transitions, JSON.parse(list.dataset.transitions || '{}')); } catch (_) { /* */ }
    // I3: 삭제 가능한 첫 상태는 서버가 넘겨준다(models.ERPDocStatuses[kind][0]).
    // 종류를 하나 더 붙일 때 이 표를 고치는 걸 잊으면 그 종류의 삭제가 통째로
    // 막히므로(거래명세서가 그랬다) 아래 표는 폴백일 뿐이다.
    const FIRST = { quote: 'draft', order: 'confirmed', statement: 'draft', invoice: 'draft' };
    const firstStatus = transitions.first || FIRST[kind] || '';
    const NEEDS_NOTE = { cancelled: true, rejected: true };
    const root = () => document.getElementById('recList') || list;
    const rows = () => Array.from(root().querySelectorAll('.rec-row[data-id]'));
    const rowById = (id) => root().querySelector('.rec-row[data-id="' + id + '"]');
    const pay = setupPayContextMenu(list, L, { attach: false });
    const tools = document.getElementById('erpTools');
    const docApi = erpApi('/api/erp/docs', L, 'save');

    // ── 필터 칩 (담당 · 거래처) — SPA 로 갈아끼워져도 살아 있게 위임
    onDoc('click', (e) => {
        const c = e.target.closest('#erpOwnerChip');
        if (c) { openPeoplePicker(c, { values: c.dataset.owner ? [c.dataset.owner] : [], allowNone: true, allowMe: true, labels: { me: L.me, none: L.ownerFilterAll || L.none }, onChange: (ids) => navigate(withParam('owner', ids[0] || '')) }); return; }
        const a = e.target.closest('#erpAccountChip');
        if (a) { openAccountPicker(a, L, a.dataset.account || '', (acct) => navigate(withParam('account', acct.id || ''))); }
    });

    // ── 저장된 보기 · 열 숨기기 (E10)
    if (tools) setupSavedViews(tools, { key: 'erp.views.' + kind, params: ['st', 'scope', 'owner', 'dept', 'account', 'contact', 'month', 'sort', 'q'] });
    const cols = setupColumnHiding(list, { key: 'erp.cols.' + kind, toolbar: tools });

    // ── 선택
    const selected = new Set();
    let anchorId = null;
    const selBar = document.getElementById('erpSelBar'), selCount = document.getElementById('erpSelCount');
    function applySelection() {
        const all = rows();
        all.forEach((r) => { const on = selected.has(r.dataset.id); r.classList.toggle('selected', on); const cb = r.querySelector('.cal-list-sel'); if (cb) cb.checked = on; });
        root().classList.toggle('has-selection', selected.size > 0);
        const head = root().querySelector('.cal-list-selall');
        if (head) { head.checked = all.length > 0 && selected.size === all.length; head.indeterminate = selected.size > 0 && selected.size < all.length; }
        if (selBar) {
            selBar.hidden = selected.size === 0;
            if (selCount) selCount.textContent = fmt(L.selectedCount, selected.size);
            const del = selBar.querySelector('[data-act="sel-delete"]');
            if (del) { const ok = targets().every(deletable); del.disabled = !ok; del.title = ok ? '' : (L.deleteNotDraftRow || ''); }
        }
    }
    function clearSelection() { if (!selected.size) return; selected.clear(); applySelection(); }
    function targets() { return Array.from(selected).map(rowById).filter(Boolean); }
    function deletable(row) { return !!firstStatus && row.dataset.status === firstStatus && !row.dataset.locked && !row.dataset.deleted; }
    function toggle(row) { if (selected.has(row.dataset.id)) selected.delete(row.dataset.id); else { selected.add(row.dataset.id); anchorId = row.dataset.id; } applySelection(); }
    function rangeTo(row) {
        const all = rows(); const a = all.findIndex((r) => r.dataset.id === anchorId), b = all.indexOf(row);
        if (a < 0) { toggle(row); return; }
        for (let i = Math.min(a, b); i <= Math.max(a, b); i += 1) selected.add(all[i].dataset.id);
        applySelection();
    }
    onDoc('change', (e) => {
        if (e.target.closest('.cal-list-selall') && root().contains(e.target)) { selected.clear(); if (e.target.checked) rows().forEach((r) => selected.add(r.dataset.id)); applySelection(); return; }
        const cb = e.target.closest('.cal-list-sel'); if (!cb || !root().contains(cb)) return;
        const row = cb.closest('.rec-row');
        if (cb.checked) { selected.add(row.dataset.id); anchorId = row.dataset.id; } else selected.delete(row.dataset.id);
        applySelection();
    });
    // SPA 이동(navigated) 과 되돌리기 뒤 소프트 갱신(refreshed) 모두: 사라진 행의 선택 해제 + 숨긴 열 다시 적용
    ['calendar:navigated', 'calendar:refreshed'].forEach((ev) => onDoc(ev, () => { for (const id of Array.from(selected)) if (!rowById(id)) selected.delete(id); applySelection(); if (ev === 'calendar:refreshed' && cols) cols.apply(); }));

    // ── 행 클릭: 체크 칸 · 담당 셀 · 상태 필은 자체 동작, 그 외는 열기 (setupRowOpen 이 처리)
    onDoc('click', (e) => {
        if (!root().contains(e.target)) return;
        const row = e.target.closest('.rec-row[data-id]'); if (!row) return;
        if (e.target.closest('.cal-list-check')) { if (e.target.tagName !== 'INPUT') { e.preventDefault(); toggle(row); } return; } // 체크박스 자체는 change 로 처리
        if (e.target.closest('[data-act="status"]')) { e.preventDefault(); e.stopPropagation(); openStatusMenu(row, e.target.closest('[data-act="status"]')); return; }
        if (e.target.closest('[data-act="restore"]')) { e.preventDefault(); e.stopPropagation(); restoreDoc(row); return; }
        if (e.target.closest('[data-act="owner"]') && !e.target.closest('a')) { e.preventDefault(); e.stopPropagation(); openOwner(row); return; }
        if (e.shiftKey) { e.preventDefault(); rangeTo(row); return; }
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); toggle(row); }
    }, true);

    // ── 헤더 우클릭: 오름차순 · 내림차순 · 열 숨기기 (spec 2.1.8). capture 단계에서 먼저 열어 saved-views 의 기본 메뉴(열 숨기기만)를 대신한다
    onDoc('contextmenu', (e) => {
        const th = e.target.closest && e.target.closest('.rec-head .rec-th[href]'); if (!th || !root().contains(th)) return;
        const attr = Array.from(th.attributes).find((a) => a.name.startsWith('data-col-')); if (!attr) return;
        const key = attr.name.slice('data-col-'.length);
        e.preventDefault();
        const items = [{ label: L.sortAsc || '오름차순', onClick: () => navigate(withParam('sort', key)) }, { label: L.sortDesc || '내림차순', onClick: () => navigate(withParam('sort', key + '_desc')) }];
        const extra = cols ? cols.items(th) : [];
        if (extra.length) items.push({ separator: true }, ...extra);
        showContextMenu(e.clientX, e.clientY, items);
    }, true);

    // ── 상태 변경 (허용 전이만)
    function nextKeys(row) { return (transitions.table[row.dataset.status] || []).slice(); }
    function statusItems(row) {
        const items = [];
        if (kind === 'invoice' && row.dataset.status === 'issued' && num(row.dataset.balance) > 0 && canPay) items.push({ label: '💰 ' + (L.payFull || '전액 입금'), onClick: () => setStatus([row], 'paid', '') });
        nextKeys(row).forEach((k) => {
            if (kind === 'invoice' && k === 'cancelled' && num(row.dataset.paid) > 0) return;
            const label = transitions.labels[k] || k;
            if (NEEDS_NOTE[k]) items.push({ label, danger: true, onClick: () => reasonPop(row.querySelector('[data-act="status"]') || row, L, { title: label + ' · ' + (L.cancelReason || '사유'), okLabel: label, danger: true, onSubmit: (v) => setStatus([row], k, v) }) });
            else items.push({ label, onClick: () => setStatus([row], k, '') });
        });
        return items;
    }
    function openStatusMenu(row, anchor) {
        const items = statusItems(row);
        if (!items.length) items.push({ label: transitions.labels[row.dataset.effective] || row.dataset.status, disabled: true });
        const r = anchor.getBoundingClientRect();
        showContextMenu(r.left, r.bottom + 4, items);
    }
    function setStatus(rowsSel, status, note) {
        if (rowsSel.length === 1) {
            return post('/api/erp/docs/' + rowsSel[0].dataset.id + '/status', note ? { status, note } : { status }, L).then(() => { clearSelection(); refreshList(); }).catch((e) => fail(L, e));
        }
        return post('/api/erp/docs/bulk-status', { ids: rowsSel.map((r) => r.dataset.id).join(','), status, note: note || '' }, L).then((res) => {
            const skipped = (res.skipped || []).length;
            showToast(fmt(L.bulkDone, (res.ok || []).length) + (skipped ? ' · ' + fmt(L.bulkSkipped, skipped) : ''), { error: skipped > 0 && !(res.ok || []).length });
            clearSelection(); refreshList();
        }).catch((e) => fail(L, e));
    }
    // 선택바 상태 변경 ▾: 선택된 행들의 허용 전이 합집합
    function bulkStatusItems(rs, anchor) {
        const keys = []; rs.forEach((r) => nextKeys(r).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
        const items = [];
        if (kind === 'invoice' && canPay && rs.some((r) => r.dataset.status === 'issued' && num(r.dataset.balance) > 0)) items.push({ label: '💰 ' + (L.payFull || '전액 입금'), onClick: () => setStatus(rs, 'paid', '') });
        keys.forEach((k) => {
            const label = transitions.labels[k] || k;
            if (NEEDS_NOTE[k]) items.push({ label, danger: true, onClick: () => reasonPop(anchor.closest('#erpSelBar') ? aboveAnchor(anchor, 160) : anchor, L, { title: label + ' · ' + (L.cancelReason || '사유'), okLabel: label, danger: true, onSubmit: (v) => setStatus(rs, k, v) }) });
            else items.push({ label, onClick: () => setStatus(rs, k, '') });
        });
        return items;
    }
    function bulkStatusMenu(anchor) {
        const rs = targets(); if (!rs.length) return;
        const items = bulkStatusItems(rs, anchor);
        if (!items.length) items.push({ label: '—', disabled: true });
        const r = anchor.getBoundingClientRect();
        showContextMenu(r.left, r.top - 8, items, { placement: 'top' }); // 선택바 위로 (메뉴 높이를 재서 자리 잡음)
    }

    // ── 담당 직원 (셀 클릭 / O / M / 선택바 담당 ▾) — 즉시 패치 후 실행 취소 스택에 기록 (토스트 버튼 = Ctrl+Z 와 같은 명령)
    function patchOwner(row, id) {
        const prev = row.dataset.owner || '', next = id || '';
        if (prev === next) return Promise.resolve();
        return docApi.patch(row.dataset.id, { owner_id: next })
            .then(() => record(patchCommand(row.dataset.id, { owner_id: prev }, { owner_id: next }, docApi), (L.ownerChanged || '담당을 바꿨습니다') + ' · ' + (row.dataset.number || '')))
            .catch((e) => fail(L, e));
    }
    function openOwner(row, anchor) {
        const cell = anchor || row.querySelector('[data-act="owner"]') || row;
        openPeoplePicker(cell, { values: row.dataset.owner ? [row.dataset.owner] : [], allowNone: true, allowMe: true, labels: { me: L.me, none: L.none }, onChange: (ids) => patchOwner(row, ids[0] || '') });
    }
    // 일괄 담당 변경: 서버 bulk-owner 한 번, 되돌리기는 행별 patch 의 composite (건너뛴 행 제외)
    function bulkOwnerTo(rs, ownerId) {
        const next = ownerId || '';
        const prevOf = {}; rs.forEach((r) => { prevOf[r.dataset.id] = r.dataset.owner || ''; });
        return post('/api/erp/docs/bulk-owner', { ids: rs.map((r) => r.dataset.id).join(','), owner_id: next, kind }, L).then((res) => {
            const ok = res.ok || [], skipped = (res.skipped || []).length;
            const msg = fmt(L.bulkDone, ok.length) + (skipped ? ' · ' + fmt(L.bulkSkipped, skipped) : '');
            clearSelection();
            const cmds = ok.filter((id) => prevOf[id] !== next).map((id) => patchCommand(id, { owner_id: prevOf[id] }, { owner_id: next }, docApi));
            if (!cmds.length) { showToast(msg, {}); return refreshList(); }
            return record(composite(cmds), msg);
        }).catch((e) => fail(L, e));
    }
    function bulkOwner(anchor) {
        const rs = targets(); if (!rs.length) return;
        openPeoplePicker(anchor.closest('#erpSelBar') ? aboveAnchor(anchor) : anchor, { values: [], allowNone: true, allowMe: true, labels: { me: L.me, none: L.none }, onChange: (ids) => bulkOwnerTo(rs, ids[0] || '') });
    }

    // ── 삭제 (초안만; 소프트) · 복원 · 복제 · 전환
    function removeDocs(rs) {
        const ok = rs.filter(deletable); if (!ok.length) { showToast(L.deleteNotDraftRow || '', { error: true }); return; }
        appConfirm({ message: L.deleteConfirm, detail: ok.map((r) => r.dataset.number).join(', '), okLabel: L.delete, cancelLabel: L.cancel, danger: true }).then((yes) => {
            if (!yes) return;
            Promise.all(ok.map((r) => docApi.remove(r.dataset.id).then(() => r.dataset.id, () => null))).then((ids) => {
                const done = ids.filter(Boolean);
                clearSelection();
                if (!done.length) { showToast(L.failed || 'failed', { error: true }); return refreshList(); }
                // 삭제는 소프트 — 되돌리기(토스트 · Ctrl+Z) 는 restore, 다시 실행은 delete
                return record(composite(done.map((id) => deleteCommand(id, docApi))), (L.deletedToast || '삭제했습니다') + ' · ' + done.length);
            });
        });
    }
    function restoreDoc(row) {
        docApi.restore(row.dataset.id).then(() => record(createCommand(row.dataset.id, docApi), (L.restored || '복원했습니다') + ' · ' + (row.dataset.number || ''))).catch((e) => fail(L, e));
    }
    function duplicateDoc(row) {
        post('/api/erp/docs/' + row.dataset.id + '/duplicate', {}, L).then((d) => navigate('/erp/docs/' + d.kind + '/' + d.id)).catch((e) => fail(L, e));
    }
    function convertLabel(row) { return kind === 'quote' ? (L.convertOrderLabel || '수주로 전환') : (L.convertInvoiceLabel || '청구서 발행'); }
    // I4: 발행된 거래명세서도 청구서로 넘길 수 있다 (erpConvertTargets[statement] = {invoice}; 초안은 서버가 400 으로 막는다)
    function convertible(row) { return (kind === 'quote' && (row.dataset.status === 'sent' || row.dataset.status === 'accepted')) || (kind === 'order' && (row.dataset.status === 'confirmed' || row.dataset.status === 'in_progress')) || (kind === 'statement' && row.dataset.status === 'issued'); }
    function convertDoc(row) {
        const to = kind === 'quote' ? 'order' : 'invoice';
        appConfirm({ message: to === 'order' ? L.convertOrder : L.convertInvoice, okLabel: convertLabel(row), cancelLabel: L.cancel }).then((ok) => {
            if (!ok) return;
            post('/api/erp/docs/' + row.dataset.id + '/convert', { to }, L).then((d) => {
                const href = '/erp/docs/' + d.kind + '/' + d.id;
                showToast((to === 'order' ? (L.kindOrder || '수주') : (L.kindInvoice || '청구서')) + ' ' + d.number + ' ' + (L.convertedToast || '만들었습니다'), { actionLabel: L.openChild || '열기', onAction: () => navigate(href), duration: 12000 });
                refreshList();
            }).catch((e) => fail(L, e));
        });
    }

    // ── 우클릭
    function rowMenu(row, x, y) {
        const group = selected.has(row.dataset.id) ? targets() : [row];
        const many = group.length > 1;
        const menu = [
            { label: L.open || '열기', onClick: () => openRow(row.dataset.href), hint: 'Enter' },
            { label: L.openNewTab || '새 탭에서 열기', onClick: () => window.open(row.dataset.href, '_blank') },
            { separator: true },
        ];
        if (row.dataset.deleted) {
            if (canManage) menu.push({ label: L.restore || '복원', onClick: () => restoreDoc(row) });
        } else {
            const st = many ? bulkStatusItems(group, row.querySelector('[data-act="status"]') || row) : statusItems(row);
            menu.push({ label: L.statusMenu || '상태 변경', children: st.length ? st : [{ label: transitions.labels[row.dataset.effective] || row.dataset.status, disabled: true }] });
            menu.push({ label: L.owner || '담당 직원', hint: 'O', children: [
                { label: L.me || '나에게 배정', hint: 'M', onClick: () => { const me = peopleIndex().me || ''; if (many) bulkOwnerTo(group, me); else patchOwner(row, me); } },
                { label: (L.owner || '담당 직원') + '…', onClick: () => { if (many) bulkOwner(row); else openOwner(row); } },
            ] });
            if (!many && kind !== 'invoice') menu.push({ label: '→ ' + convertLabel(row), disabled: !convertible(row), onClick: () => convertDoc(row) });
            if (!many) menu.push({ label: L.duplicate || '복제', onClick: () => duplicateDoc(row) });
            if (!many && printable) menu.push({ label: L.print || '인쇄', onClick: () => window.open(row.dataset.href + '/print?auto=1', '_blank') });
            if (!many) pay.items(row).forEach((it) => menu.push(it));
            if (canManage) menu.push({ separator: true }, { label: L.delete || '삭제', danger: true, disabled: !group.every(deletable), onClick: () => removeDocs(group), hint: 'Del' });
        }
        showContextMenu(x, y, menu);
    }
    onDoc('contextmenu', (e) => {
        if (!root().contains(e.target) || e.target.closest('.erp-pay-inline')) return;
        const row = e.target.closest('.rec-row[data-id]'); if (!row) return;
        e.preventDefault(); row.focus(); rowMenu(row, e.clientX, e.clientY);
    });

    // ── 선택바
    if (selBar) selBar.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]'); if (!b) return;
        const act = b.dataset.act;
        if (act === 'sel-status') bulkStatusMenu(b);
        else if (act === 'sel-owner') bulkOwner(b);
        else if (act === 'sel-csv') { const base = root().dataset.csvHref || ''; window.location.href = base + (base.includes('?') ? '&' : '?') + 'ids=' + encodeURIComponent(Array.from(selected).join(',')); }
        else if (act === 'sel-delete') removeDocs(targets());
        else if (act === 'sel-clear') clearSelection();
    });

    // ── 키보드
    onDoc('keydown', (e) => {
        if (inField() || e.altKey) return;
        const rs = rows(); if (!rs.length && e.key !== 'n' && e.key !== 'N') return;
        const focusRow = document.activeElement && document.activeElement.closest ? document.activeElement.closest('.rec-row[data-id]') : null;
        const cur = rs.indexOf(focusRow);
        if (e.key === 'Escape') { if (selected.size) { e.preventDefault(); clearSelection(); } return; }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && (focusRow || selected.size)) { e.preventDefault(); rs.forEach((r) => selected.add(r.dataset.id)); applySelection(); return; }
        if (e.ctrlKey || e.metaKey) return;
        if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); const n = rs[Math.min(rs.length - 1, cur + 1)]; n.focus(); if (e.shiftKey) rangeTo(n); }
        else if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); const n = rs[Math.max(0, cur - 1)]; n.focus(); if (e.shiftKey) rangeTo(n); }
        else if (e.key === 'n' || e.key === 'N') { const a = document.querySelector('.crm-tools a.btn-primary'); if (a) { e.preventDefault(); navigate(a.href); } }
        else if (!focusRow) return;
        else if (e.key === 'x' || e.key === ' ') { e.preventDefault(); toggle(focusRow); }
        else if (e.key === 'Delete' || e.key === 'Backspace') { if (!canManage) return; e.preventDefault(); removeDocs(selected.size ? targets() : [focusRow]); }
        else if (e.key === 'o' || e.key === 'O') { e.preventDefault(); if (selected.size > 1 && selected.has(focusRow.dataset.id)) bulkOwner(focusRow); else openOwner(focusRow); }
        else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); const me = peopleIndex().me || root().dataset.me; if (selected.size > 1 && selected.has(focusRow.dataset.id)) bulkOwnerTo(targets(), me); else patchOwner(focusRow, me); }
        else if (e.key === 's' || e.key === 'S') { e.preventDefault(); openStatusMenu(focusRow, focusRow.querySelector('[data-act="status"]') || focusRow); }
        else if ((e.key === 'p' || e.key === 'P') && pay.canOpen(focusRow)) { e.preventDefault(); pay.openInline(focusRow); }
    });
    applySelection();
    return { cols };
}

// 청구서 행 우클릭: 열기 · 새 탭 · 입금 기록…(행 아래 인라인 퀵 행) · 전액 입금 처리 (CanPay, 발행 + 잔액 > 0)
// opts.attach === false 면 메뉴를 직접 달지 않고 items(row) 만 내준다 (문서 목록이 자기 메뉴에 합친다).
function setupPayContextMenu(list, L, opts) {
    const o = opts || {};
    const canPay = !!list.dataset.canPay;
    const today = list.dataset.today || new Date().toISOString().slice(0, 10);
    function refresh() { navigate(location.pathname + location.search, { replace: true, keepScroll: true }); }
    function openInline(row) {
        const old = list.querySelector('.erp-pay-inline'); if (old) old.remove();
        const box = document.createElement('div'); box.className = 'erp-pay-inline';
        const head = document.createElement('div'); head.className = 'erp-pay-inline-head'; head.textContent = (L.recordPaymentMenu || '입금 기록') + ' · ' + (row.dataset.number || '') + ' · ' + (L.balance || '잔액') + ' ' + money(row.dataset.balance);
        box.appendChild(head);
        const quick = buildQuickPayRow({ docId: row.dataset.id, balance: () => num(row.dataset.balance), L, money, num, today, post, onCancel: () => box.remove(), onSaved: (d) => { box.remove(); showToast((L.paymentSaved || '') + ' · ' + (row.dataset.number || ''), {}); row.dataset.balance = d.balance; refresh(); } });
        box.appendChild(quick);
        row.insertAdjacentElement('afterend', box);
        quick._focus();
    }
    function payFull(row) {
        appConfirm({ message: (L.payFullConfirm || '') + ' ' + (row.dataset.number || '') + ' · ' + money(row.dataset.balance), okLabel: L.payFull, cancelLabel: L.cancel }).then((ok) => {
            if (!ok) return;
            post('/api/erp/docs/' + encodeURIComponent(row.dataset.id) + '/payments', { amount: row.dataset.balance, method: 'transfer', paid_on: today, client_key: (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : '' }, L)
                .then(() => { showToast((L.paymentSaved || '') + ' · ' + (row.dataset.number || ''), {}); refresh(); }).catch((e) => fail(L, e));
        });
    }
    function canOpen(row) { return canPay && row.dataset.status === 'issued' && num(row.dataset.balance) > 0; }
    function items(row) {
        if (!canOpen(row)) return [];
        return [{ separator: true }, { label: (L.recordPaymentMenu || '입금 기록') + '…', onClick: () => openInline(row), hint: 'P' }, { label: L.payFullMenu || '전액 입금 처리', onClick: () => payFull(row) }];
    }
    if (o.attach !== false) {
        list.addEventListener('contextmenu', (e) => {
            if (e.target.closest('.erp-pay-inline')) return;
            const row = e.target.closest('.rec-row[data-id]'); if (!row) return;
            e.preventDefault();
            showContextMenu(e.clientX, e.clientY, [
                { label: L.open || '열기', onClick: () => openRow(row.dataset.href), hint: 'Enter' },
                { label: L.openNewTab || '새 탭에서 열기', onClick: () => window.open(row.dataset.href, '_blank') },
            ].concat(items(row)));
        });
        list.addEventListener('keydown', (e) => {
            const row = e.target.closest('.rec-row[data-id]'); if (!row || inField() || e.ctrlKey || e.metaKey || e.altKey) return;
            if ((e.key === 'p' || e.key === 'P') && canOpen(row)) { e.preventDefault(); openInline(row); }
        });
    }
    return { items, openInline, payFull, canOpen };
}

// ── 품목 마스터 (E8): 제자리 편집 · 퀵 애드(리로드 없음) · 정렬 헤더 · ✕ → 되돌리기 · 삭제됨 보기 복원 · 우클릭 ──
function setupItemList(list, L) {
    const readonly = !!list.dataset.readonly, deletedView = !!list.dataset.deleted;
    const TAX = { taxable: list.dataset.lTaxable || L.taxTaxable || '과세 10%', zero: list.dataset.lZero || L.taxZero || '영세 0%', exempt: list.dataset.lExempt || L.taxExempt || '면세' };
    const tools = document.getElementById('erpTools');
    if (tools) setupSavedViews(tools, { key: 'erp.views.items', params: ['st', 'q'] });
    const cols = setupColumnHiding(list, { key: 'erp.cols.items', toolbar: tools });
    const itemApi = erpApi('/api/erp/items', L, 'patch');
    onDoc('calendar:refreshed', () => { if (cols) cols.apply(); });
    // C-4 §8.3: CSV 가져오기 확정 뒤 돌아온 경우 결과 토스트 (crm.js 의 deletedFlash 와 같은 패턴)
    const importedFlash = new URLSearchParams(location.search).get('imported');
    if (importedFlash !== null) {
        const updatedFlash = new URLSearchParams(location.search).get('updated') || '0';
        const u = new URL(location.href); u.searchParams.delete('imported'); u.searchParams.delete('updated');
        // `history` 는 이 모듈에서 calendar-history.js 의 실행 취소 스택이다(:11).
        // 브라우저 History API 를 쓰는 곳은 이 파일에서 여기 한 곳뿐이라 반드시
        // window. 을 붙여야 한다 — 안 붙이면 replaceState 가 없어 TypeError 가 나고
        // setupItemList 이후의 초기화가 통째로 죽는다 (최종 리뷰 F1).
        window.history.replaceState(window.history.state, '', u.pathname + (u.search || ''));
        showToast((L.importResult || '신규 {0}건 · 갱신 {1}건').replace('{0}', importedFlash).replace('{1}', updatedFlash), {});
    }
    // 정렬 헤더 (클라이언트, 전체 로드된 목록)
    let sortKey = '', sortDesc = false;
    function sortRows(key, desc) {
        if (typeof desc === 'boolean') { sortKey = key; sortDesc = desc; } else if (sortKey === key) sortDesc = !sortDesc; else { sortKey = key; sortDesc = false; }
        const rs = Array.from(list.querySelectorAll('.rec-row'));
        const val = (r) => (key === 'unit_price' ? num(r.dataset.unitPrice) : key === 'name' ? r.querySelector('[data-field="name"]').value : key === 'tax_type' ? r.dataset.taxType : (r.querySelector('[data-field="' + key + '"]') || { value: r.dataset[key] || '' }).value);
        rs.sort((a, b) => { const x = val(a), y = val(b); const c = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'ko'); return sortDesc ? -c : c; });
        const anchor = list.querySelector('.rec-empty, .rec-quick, .rec-add');
        rs.forEach((r) => list.insertBefore(r, anchor));
        list.querySelectorAll('.rec-th-sort').forEach((h) => { const on = h.dataset.sort === key; h.classList.toggle('active', on); let i = h.querySelector('i'); if (on) { if (!i) { i = document.createElement('i'); h.appendChild(i); } i.textContent = sortDesc ? '↓' : '↑'; } else if (i) i.remove(); });
    }
    list.addEventListener('click', (e) => { const h = e.target.closest('.rec-th-sort'); if (!h) return; e.preventDefault(); sortRows(h.dataset.sort); });
    // 헤더 우클릭: 오름차순 · 내림차순 · 열 숨기기 (spec 2.1.8)
    onDoc('contextmenu', (e) => {
        const h = e.target.closest && e.target.closest('.rec-head .rec-th-sort'); if (!h || !list.contains(h)) return;
        e.preventDefault();
        const items = [{ label: L.sortAsc || '오름차순', onClick: () => sortRows(h.dataset.sort, false) }, { label: L.sortDesc || '내림차순', onClick: () => sortRows(h.dataset.sort, true) }];
        const extra = cols ? cols.items(h) : [];
        if (extra.length) items.push({ separator: true }, ...extra);
        showContextMenu(e.clientX, e.clientY, items);
    }, true);
    // 세율 필 (E7): 과세 10% / 영세 0% / 면세 → tax_type 패치 (유형이 세율을 강제)
    list.addEventListener('click', (e) => {
        const pillBtn = e.target.closest('.erp-tax-pill[data-field="tax_type"]'); if (!pillBtn || pillBtn.disabled || readonly) return;
        const row = pillBtn.closest('.rec-row');
        const options = Object.keys(TAX).map((t) => ({ label: TAX[t], color: 'o8' }));
        openPillPicker(pillBtn, { options, values: [TAX[pillBtn.dataset.value] || TAX.taxable], neutral: true, required: true, placeholder: L.taxRate || '세율', onChange: (vals) => {
            const t = Object.keys(TAX).find((k) => TAX[k] === vals[0]) || 'taxable';
            post('/api/erp/items/' + row.dataset.id + '/patch', { tax_type: t }, L).then((it) => {
                pillBtn.dataset.value = it.tax_type; pillBtn.className = 'prop-pill neutral erp-tax-pill t-' + it.tax_type; pillBtn.textContent = TAX[it.tax_type] || it.tax_type; row.dataset.taxType = it.tax_type;
            }).catch((err) => fail(L, err));
        } });
    });
    // 삭제됨 보기: 복원
    list.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act="restore-item"]'); if (!b) return;
        const row = b.closest('.rec-row');
        itemApi.restore(row.dataset.id).then(() => record(createCommand(row.dataset.id, itemApi), (L.itemRestored || '품목을 복원했습니다') + ' · ' + (row.dataset.name || ''))).catch((err) => fail(L, err));
    });
    list.addEventListener('keydown', (e) => {
        const row = e.target.closest('.rec-row'); if (!row || inField()) return;
        const rs = Array.from(list.querySelectorAll('.rec-row')); const i = rs.indexOf(row);
        if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); (rs[i + 1] || row).focus(); }
        else if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); (rs[i - 1] || row).focus(); }
        else if (!readonly && e.key === 'Enter') { e.preventDefault(); row.querySelector('[data-field="name"]').focus(); }
        else if (!readonly && e.key === 'Delete') { e.preventDefault(); removeItem(row); }
    });
    if (readonly) return;
    // 제자리 편집: 포커스 시점 값을 기억해 두고 저장 뒤 실행 취소 스택에 기록 (화면은 갱신하지 않는다 — Ctrl+Z 때만)
    list.addEventListener('focusin', (e) => { const el = e.target.closest('[data-field]'); if (el && el.type !== 'checkbox') el.dataset.prev = el.value; });
    list.addEventListener('change', (e) => {
        const el = e.target.closest('[data-field]'); if (!el) return;
        const row = el.closest('.rec-row'); if (!row) return;
        const value = el.type === 'checkbox' ? (el.checked ? '1' : '0') : el.value;
        const prev = el.type === 'checkbox' ? (value === '1' ? '0' : '1') : (el.dataset.prev != null ? el.dataset.prev : (row.dataset[el.dataset.field] || ''));
        post('/api/erp/items/' + row.dataset.id + '/patch', { [el.dataset.field]: value }, L).then((it) => {
            if (prev !== value) history.push(patchCommand(row.dataset.id, { [el.dataset.field]: prev }, { [el.dataset.field]: value }, itemApi));
            el.dataset.prev = el.type === 'checkbox' ? '' : el.value;
            if (el.dataset.field === 'unit_price') { el.value = money(it.unit_price); row.dataset.unitPrice = it.unit_price; }
            if (el.dataset.field === 'active') row.classList.toggle('inactive', !it.active);
            if (el.dataset.field === 'name') row.dataset.name = it.name;
            el.classList.remove('err'); el.classList.add('saved'); setTimeout(() => el.classList.remove('saved'), 800);
        }).catch((err) => { el.classList.add('err'); fail(L, err); });
    });
    list.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.closest('.crm-inline')) { e.preventDefault(); e.target.blur(); } });
    // ✕ / Del → 소프트 삭제 + 되돌리기 토스트 (복원)
    function removeItem(row) {
        const id = row.dataset.id, name = row.dataset.name || '';
        itemApi.remove(id).then(() => record(deleteCommand(id, itemApi), (L.itemDeletedUndo || '품목을 삭제했습니다') + ' · ' + name)).catch((err) => fail(L, err));
    }
    // 새 행 DOM (템플릿 행과 같은 마크업)
    function itemRowEl(it) {
        const row = document.createElement('div'); row.className = 'rec-row' + (it.active ? '' : ' inactive'); row.tabIndex = 0;
        row.dataset.id = it.id; row.dataset.name = it.name || ''; row.dataset.taxType = it.tax_type || 'taxable'; row.dataset.code = it.code || ''; row.dataset.spec = it.spec || ''; row.dataset.unit = it.unit || ''; row.dataset.unitPrice = String(it.unit_price || 0); row.dataset.active = it.active ? '1' : '';
        const cell = (cls, col, child) => { const s = document.createElement('span'); s.className = cls; s.setAttribute('data-col-' + col, ''); if (child) s.appendChild(child); row.appendChild(s); return s; };
        const input = (field, cls, value, max, ph) => { const i = document.createElement('input'); i.className = 'crm-inline ' + cls; i.dataset.field = field; i.value = value || ''; i.maxLength = max; if (ph) i.placeholder = ph; return i; };
        cell('rec-cell-code', 'code', input('code', 'mono', it.code, 60, '—'));
        cell('rec-cell-name', 'name', input('name', '', it.name, 200));
        cell('rec-cell-spec', 'spec', input('spec', 'sub', it.spec, 100, '—'));
        cell('rec-cell-unit', 'unit', input('unit', 'sub', it.unit, 20, 'EA'));
        const price = input('unit_price', 'num', money(it.unit_price || 0), 20); price.inputMode = 'numeric';
        cell('rec-cell-price', 'price', price);
        const pill = document.createElement('button'); pill.type = 'button'; pill.className = 'prop-pill neutral erp-tax-pill t-' + (it.tax_type || 'taxable'); pill.dataset.field = 'tax_type'; pill.dataset.value = it.tax_type || 'taxable'; pill.textContent = TAX[it.tax_type] || TAX.taxable;
        cell('rec-cell-tax', 'tax', pill);
        const me = personOf(peopleIndex().me);
        cell('rec-cell-by', 'by', me ? personEl(me, 'xs') : null);
        const x = document.createElement('button'); x.type = 'button'; x.className = 'crm-x'; x.dataset.act = 'del-item'; x.setAttribute('aria-label', (l10n().remove) || '삭제'); x.tabIndex = -1; x.textContent = '✕';
        const xc = document.createElement('span'); xc.className = 'rec-cell-x'; xc.style.textAlign = 'right'; xc.appendChild(x); row.appendChild(xc);
        return row;
    }
    function duplicateItem(row) {
        const f = (k) => row.querySelector('[data-field="' + k + '"]').value;
        post('/api/erp/items', { name: f('name'), code: '', unit: f('unit'), spec: f('spec'), unit_price: String(num(f('unit_price'))), tax_type: row.dataset.taxType || 'taxable' }, L).then((it) => { const el = itemRowEl(it); row.insertAdjacentElement('afterend', el); el.querySelector('[data-field="name"]').focus(); }).catch((err) => fail(L, err));
    }
    function toggleActive(row) {
        const next = row.dataset.active ? '0' : '1';
        post('/api/erp/items/' + row.dataset.id + '/patch', { active: next }, L).then((it) => {
            row.dataset.active = it.active ? '1' : '';
            row.classList.toggle('inactive', !it.active);
            history.push(patchCommand(row.dataset.id, { active: next === '1' ? '0' : '1' }, { active: next }, itemApi));
        }).catch((err) => fail(L, err));
    }
    list.addEventListener('click', (e) => { const b = e.target.closest('[data-act="del-item"]'); if (!b) return; removeItem(b.closest('.rec-row')); });
    // 퀵 애드: Enter → 행 삽입 + 단가 셀 포커스, 입력 칸은 남긴다 (리로드 없음)
    function openQuick() {
        if (deletedView) return;
        if (list.querySelector('.rec-quick')) { list.querySelector('.rec-quick input').focus(); return; }
        const box = document.createElement('div'); box.className = 'rec-quick';
        const input = document.createElement('input'); input.type = 'text'; input.maxLength = 200; input.placeholder = list.dataset.lAdd || L.itemAdd || '';
        box.appendChild(input);
        list.insertBefore(box, list.querySelector('.rec-add'));
        input.focus();
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { box.remove(); return; }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const name = input.value.trim(); if (!name) return;
            input.disabled = true;
            post('/api/erp/items', { name }, L).then((it) => {
                const empty = list.querySelector('.rec-empty'); if (empty) empty.remove();
                const row = itemRowEl(it);
                list.insertBefore(row, box);
                input.value = ''; input.disabled = false;
                const kpi = document.querySelector('.crm-kpis b'); if (kpi) kpi.textContent = String(list.querySelectorAll('.rec-row').length);
                row.querySelector('[data-field="unit_price"]').focus(); row.querySelector('[data-field="unit_price"]').select();
            }).catch((err) => { input.disabled = false; fail(L, err); });
        });
        input.addEventListener('blur', () => setTimeout(() => { if (!input.value.trim() && box.isConnected && !list.contains(document.activeElement)) box.remove(); }, 150));
    }
    const add = document.getElementById('recAdd'); if (add) add.addEventListener('click', openQuick);
    list.addEventListener('contextmenu', (e) => {
        if (e.target.closest('input') || e.defaultPrevented) return;
        const row = e.target.closest('.rec-row');
        if (!row) { if (e.target.closest('.rec-th')) return; e.preventDefault(); showContextMenu(e.clientX, e.clientY, [{ label: '＋ ' + (L.newItem || list.dataset.lAdd || ''), onClick: openQuick, hint: 'N' }]); return; }
        e.preventDefault();
        if (deletedView) { showContextMenu(e.clientX, e.clientY, [{ label: L.restore || '복원', onClick: () => row.querySelector('[data-act="restore-item"]').click() }]); return; }
        const active = !!row.dataset.active;
        showContextMenu(e.clientX, e.clientY, [
            { label: L.menuDuplicate || '복제', onClick: () => duplicateItem(row) },
            { label: active ? (L.menuDeactivate || '비활성') : (L.menuActivate || '활성'), onClick: () => toggleActive(row) },
            { separator: true },
            { label: L.delete || '삭제', danger: true, onClick: () => removeItem(row), hint: 'Del' },
        ]);
    });
    onDoc('keydown', (e) => { if (!inField() && !e.ctrlKey && !e.metaKey && (e.key === 'n' || e.key === 'N')) { e.preventDefault(); openQuick(); } });
}

// C-4 §8.3: 품목 상세(#erpItemPrices)의 거래처별 단가 카드 — 추가/삭제는
// /api/erp/items/:id/prices[/…]가 JSON 을 돌려주므로(다른 ERP API 와 동일한
// 관례) 평범한 폼 제출 대신 fetch 로 다룬다. account_name 은 사용자가 지은
// 거래처 이름이라 innerHTML 이 아니라 textContent 로만 채운다.
function setupItemPrices(card, L) {
    const itemId = card.dataset.item;
    const list = document.getElementById('erpItemPriceList');
    function renderPrices(prices) {
        if (!list) return;
        list.innerHTML = '';
        if (!prices || !prices.length) {
            const e = document.createElement('div'); e.className = 'rec-empty'; e.textContent = '—';
            list.appendChild(e);
            return;
        }
        prices.forEach((p) => {
            const row = document.createElement('div'); row.className = 'erp-item-price-row'; row.dataset.pid = p.id;
            const acct = document.createElement('span'); acct.className = 'erp-item-price-acct'; acct.textContent = p.account_name;
            const amt = document.createElement('span'); amt.className = 'erp-item-price-amt mono'; amt.textContent = money(p.unit_price);
            row.appendChild(acct); row.appendChild(amt);
            if (card.querySelector('#erpItemPriceAdd')) {
                const x = document.createElement('button'); x.type = 'button'; x.className = 'crm-x erp-item-price-x'; x.dataset.act = 'del-price'; x.setAttribute('aria-label', 'remove'); x.textContent = '✕';
                row.appendChild(x);
            }
            list.appendChild(row);
        });
    }
    if (list) list.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-act="del-price"]'); if (!btn) return;
        const row = btn.closest('.erp-item-price-row'); const pid = row && row.dataset.pid;
        if (!pid) return;
        post('/api/erp/items/' + encodeURIComponent(itemId) + '/prices/' + encodeURIComponent(pid) + '/delete', {}, L)
            .then((data) => renderPrices(data.prices))
            .catch((err) => fail(L, err));
    });
    const addBtn = document.getElementById('erpItemPriceAddBtn');
    if (addBtn) addBtn.addEventListener('click', () => {
        const acctSel = document.getElementById('erpItemPriceAccount');
        const input = document.getElementById('erpItemPriceInput');
        const accountId = acctSel ? acctSel.value : '';
        if (!accountId) { showToast(L.pickAccountFirst || '', { error: true }); return; }
        post('/api/erp/items/' + encodeURIComponent(itemId) + '/prices', { account_id: accountId, unit_price: String(num(input.value)) }, L)
            .then((data) => { renderPrices(data.prices); if (input) input.value = ''; if (acctSel) acctSel.value = ''; })
            .catch((err) => fail(L, err));
    });
}

// ── /admin/erp 설정 페이지 ──
export function setupERPSettings(form, L) {
    const banks = document.getElementById('erpBanks');
    function reindex() {
        Array.from(banks.querySelectorAll('.erp-bank-row:not(.head)')).forEach((r, i) => { r.dataset.index = i; r.querySelector('input[type="radio"]').value = i; });
        if (banks.querySelector('.erp-bank-row:not(.head)') && !banks.querySelector('input[type="radio"]:checked')) banks.querySelector('input[type="radio"]').checked = true;
    }
    function addBank() {
        const r = document.createElement('div'); r.className = 'erp-bank-row';
        r.innerHTML = '<span class="erp-bank-c-radio"><input type="radio" name="bank_default" value="0"></span><span class="erp-bank-c-bank"><input class="form-input" name="bank_bank" maxlength="60"></span><span class="erp-bank-c-number"><input class="form-input mono" name="bank_number" maxlength="40" inputmode="numeric"></span><span class="erp-bank-c-holder"><input class="form-input" name="bank_holder" maxlength="100"></span><span class="erp-bank-c-remove"><button type="button" class="crm-x erp-bank-remove" aria-label="remove">✕</button></span>';
        // 모바일(≤600px)에서 값 위에 라벨을 붙이는 ::before 가 읽는 data-label (erp.css) —
        // 서버가 렌더한 헤더 라벨(#erpBanks 의 data-l-*)을 그대로 재사용한다.
        r.querySelector('.erp-bank-c-bank').dataset.label = banks.dataset.lBank || '';
        r.querySelector('.erp-bank-c-number').dataset.label = banks.dataset.lNumber || '';
        r.querySelector('.erp-bank-c-holder').dataset.label = banks.dataset.lHolder || '';
        banks.appendChild(r); reindex(); r.querySelector('[name="bank_bank"]').focus();
    }
    const addBtn = document.getElementById('erpBankAdd'); if (addBtn) addBtn.addEventListener('click', addBank);
    if (banks) banks.addEventListener('click', (e) => { const b = e.target.closest('.erp-bank-remove'); if (!b) return; b.closest('.erp-bank-row').remove(); reindex(); });
    if (banks) banks.addEventListener('input', (e) => { if (e.target.name === 'bank_number') e.target.value = e.target.value.replace(/[^0-9-]/g, ''); });
    // 사업자번호: 자동 하이픈 + 검증번호 힌트
    const biz = form.querySelector('.erp-bizno'), hint = document.getElementById('erpBizNoHint');
    function validBizNo(s) {
        const d = s.replace(/-/g, ''); if (!/^\d{10}$/.test(d)) return false;
        const w = [1, 3, 7, 1, 3, 7, 1, 3, 5]; let sum = 0;
        for (let i = 0; i < 9; i += 1) sum += Number(d[i]) * w[i];
        sum += Math.floor(Number(d[8]) * 5 / 10);
        return (10 - (sum % 10)) % 10 === Number(d[9]);
    }
    if (biz) {
        biz.addEventListener('input', () => {
            const d = biz.value.replace(/\D/g, '').slice(0, 10);
            biz.value = d.length > 5 ? d.slice(0, 3) + '-' + d.slice(3, 5) + '-' + d.slice(5) : d.length > 3 ? d.slice(0, 3) + '-' + d.slice(3) : d;
            const bad = biz.value && !validBizNo(biz.value) && d.length === 10;
            biz.classList.toggle('err', bad); if (hint) hint.hidden = !bad;
        });
        biz.addEventListener('blur', () => { const bad = !!biz.value && !validBizNo(biz.value); biz.classList.toggle('err', bad); if (hint) hint.hidden = !bad; });
    }
    // 담당 직원 표시 미리보기
    const preview = document.getElementById('erpRepPreview');
    if (preview) {
        const block = preview.querySelector('.erp-rep-block');
        const me = peopleIndex().byId[peopleIndex().me];
        const apply = (mode) => {
            preview.dataset.mode = mode;
            block.hidden = mode === 'none';
            const extra = block.querySelector('.erp-rep-creator'); if (extra) extra.remove();
            if (mode === 'owner_creator' && me) { const s = document.createElement('span'); s.className = 'erp-rep-creator'; s.appendChild(document.createTextNode((L.createdBy || '작성') + ' ')); s.appendChild(personEl(me, 'sm')); block.appendChild(s); }
        };
        form.querySelectorAll('input[name="rep_display"]').forEach((r) => r.addEventListener('change', () => apply(r.value)));
        apply(preview.dataset.mode || 'owner');
    }
    form.querySelectorAll('.erp-seg-radio label').forEach((lab) => lab.addEventListener('click', () => { form.querySelectorAll('.erp-seg-radio label').forEach((x) => x.classList.toggle('active', x === lab)); }));
    form.addEventListener('submit', (e) => { if (biz && biz.value && !validBizNo(biz.value)) { e.preventDefault(); biz.focus(); showToast(L.bizNoInvalid || '', { error: true }); } });
    // F9: 페이지에 폼이 두 개(회사 정보 · 번호 규칙)라 Ctrl+S 는 포커스가 있는
    // 필드의 폼을 제출해야 한다 — 무조건 #erpSettings 로 보내면 번호 규칙
    // 카드를 편집하다 Ctrl+S 를 눌렀을 때 그 입력이 조용히 버려지고 엉뚱한
    // (회사 정보) 폼이 제출돼 페이지가 새로 고쳐진다.
    onDoc('keydown', (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); const f = (e.target && e.target.closest && e.target.closest('form')) || form; f.requestSubmit(); } });
    // C-4 §8.1: 접두어·자릿수·초기화·다음 번호를 바꾸면 미리보기를 즉시 갱신
    // (서버의 models.ERPNumberRule.Format 과 같은 공식 — 저장 전 미리보기라
    // 진짜 시퀀스 값은 아니다).
    const numbering = document.getElementById('erpNumbering');
    if (numbering) {
        const redraw = (row) => {
            const prefix = (row.querySelector('.erp-num-prefix').value || '').toUpperCase();
            const digits = Math.min(6, Math.max(3, Number(row.querySelector('.erp-num-digits').value) || 4));
            const reset = row.querySelector('.erp-num-reset').value;
            const now = new Date();
            const y = String(now.getFullYear()), m = String(now.getMonth() + 1).padStart(2, '0');
            const period = reset === 'month' ? y + m : reset === 'never' ? '' : y;
            const seq = Math.max(1, Number(row.querySelector('.erp-num-next').value) || 1);
            row.querySelector('[data-sample]').textContent = prefix + '-' + (period ? period + '-' : '') + String(seq).padStart(digits, '0');
        };
        numbering.querySelectorAll('.erp-props[data-kind]').forEach((row) => {
            row.addEventListener('input', () => redraw(row));
            row.addEventListener('change', () => redraw(row));
            // F2: 초기화 주기를 바꾸면 다음 번호는 **새 기간**의 것이다. 화면에
            // 미리 채워져 있던 값은 옛 기간의 시퀀스라 그대로 제출하면 새 버킷이
            // 1 이 아니라 그 값에서 시작한다 — 비워서 "지정 없음"(서버가 건너뛴다,
            // updateERPNumbering)으로 되돌린다. 운영자가 다시 값을 넣으면 그 값이
            // 쓰이고 하한 검사도 그대로 걸린다.
            const reset = row.querySelector('.erp-num-reset');
            if (reset) reset.addEventListener('change', () => { const n = row.querySelector('.erp-num-next'); if (n) n.value = ''; redraw(row); });
        });
    }
    // C-4 §8.2: 결재선 라디오(approval_mode)가 유일한 컨트롤이다 — 바뀌면
    // 기준 금액·대상 문서 블록을 보이거나 숨긴다 (fix round 1, F1: 레거시
    // 체크박스는 카드에서 제거되었다 — 그 값은 approval_mode 가 아예 없는
    // 요청에서만 서버가 본다).
    const approval = document.getElementById('erpApprovalPolicy');
    if (approval) {
        const thresholdRow = approval.querySelector('.erp-approval-threshold');
        const kindsBox = approval.querySelector('.erp-approval-kinds');
        const kindsRow = kindsBox && kindsBox.closest('.cal-prop');
        const apply = (mode) => {
            approval.dataset.mode = mode;
            if (thresholdRow) thresholdRow.hidden = mode !== 'threshold';
            if (kindsRow) kindsRow.hidden = mode === 'off';
        };
        approval.querySelectorAll('input[name="approval_mode"]').forEach((r) => r.addEventListener('change', () => apply(r.value)));
        apply(approval.dataset.mode || 'off');
    }
}

// 세금계산서 카드 배선 (C-3 §6.2): 발행 여부·발행일·승인번호·수단·메모를 저장하고
// 헤더 칩을 응답으로 제자리 갱신한다. onSaved(version) 은 문서 편집기의 낙관적
// 잠금(if_version)을 이 저장이 올린 새 버전과 맞춰 둔다.
function setupTaxInvoiceCard(card, { post, L, docId, version, onSaved }) {
    if (!card || !card.dataset.canEdit) return;
    const btn = card.querySelector('#erpTaxSave');
    if (!btn) return;
    attachDatePicker(card.querySelector('#erpTaxDateBtn'), card.querySelector('#erpTaxDate'), { placeholder: L.pickDate });
    syncDateLabel(card.querySelector('#erpTaxDateBtn'), card.querySelector('#erpTaxDate'), L.pickDate);
    btn.addEventListener('click', () => {
        const body = {
            issued: card.querySelector('#erpTaxIssued').checked ? '1' : '0',
            date: card.querySelector('#erpTaxDate').value,
            no: card.querySelector('#erpTaxNo').value.trim(),
            method: card.querySelector('#erpTaxMethod').value,
            memo: card.querySelector('#erpTaxMemo').value.trim(),
            if_version: String(version()),
        };
        post('/api/erp/docs/' + encodeURIComponent(docId) + '/tax-invoice', body, L)
            .then((data) => { card.querySelector('#erpTaxChip').textContent = data.chip; if (onSaved) onSaved(data.version); showToast(L.saved || '', {}); })
            .catch((e) => showToast((e && e.message) || L.failed || '', { error: true }));
    });
}
